import {
  Component,
  ViewChild,
  HostListener,
  ElementRef,
  OnInit,
  HostBinding,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Chart } from 'angular-highcharts';
import { ToastrService } from 'ngx-toastr';
import { NewPlanPopupComponent } from 'src/app/shared/components/new-plan-popup/new-plan-popup.component';
import { ToasterMessages } from 'src/app/shared/constants/constants';
import { CargoheatingCommonService } from 'src/app/shared/service/cargo-heating-service/cargoheating-common.service';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';
import {
  CARGO_HEATING_SEGMENTS,
  matchesCargoHeatingSegment,
} from '../../cargo-heating-segments';
import html2canvas from 'html2canvas';
import { Observable, Subscription, catchError, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { DEMO_VESSEL_IMOS } from 'src/app/shared/helpers/heating-table-dynamic-extract.util';
import {
  HeatplanConsumptionRow,
  MasterConsumptionRow,
  VoyageConsumptionSummary,
  extractHeatplanLogs,
  extractMasterVoyageLogs,
  mergeHeatplanLogsByDate,
  summariseVoyageConsumption,
} from 'src/app/shared/helpers/heatplan-consumption.util';
import {
  MasterTempRow,
  TankProfileTempFields,
  averageLoadTemp,
  extractTankProfile,
  lastDayCargoTemp,
  totalCargoQuantity,
} from 'src/app/shared/helpers/voyage-cargo-temp.util';

export interface VoyageHeatplan {
  plan_number: number;
  start_date: string;
  end_date: string;
  days: number;
}

export type HeatplanState = 'done' | 'active' | 'upcoming';

/** One heat plan laid out on the overview timeline (offsets in % of the covered span). */
export interface HeatplanTimelineRow extends VoyageHeatplan {
  state: HeatplanState;
  left: number;
  width: number;
}

/**
 * Heat plans cover 7 days and a new one goes out every 5th day, so the next plan is due
 * 2 days before the latest plan's last day.
 */
export const HEATPLAN_DUE_LEAD_DAYS = 2;
/** "Due soon" window: this many days or fewer before the due date. */
export const HEATPLAN_DUE_SOON_DAYS = 2;

/** "Today" for heat plan dues and port calls is the IST calendar date (UTC+5:30, no daylight saving). */
const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;

/** 'port': today is a discharge port arrival day or the day before, so no plan is due while the vessel is in port. */
export type HeatplanDueLevel = 'overdue' | 'today' | 'soon' | 'upcoming' | 'missing' | 'none' | 'port';

export interface HeatplanDue {
  level: HeatplanDueLevel;
  /** Days from today to the due date; negative once overdue, null without a heat plan or while in port. */
  daysLeft: number | null;
  dueTime: number | null;
  latestPlanNumber: number | null;
  latestPlanEnd: string | null;
  label: string;
  /**
   * Discharge port call around today (level 'port' only). `arriving`: arrival is tomorrow;
   * otherwise arrival is today and the vessel is in port.
   */
  port: { name: string; arrival: number; arriving: boolean } | null;
}

export type PortStopState = 'done' | 'next' | 'upcoming';

export interface PortStop {
  kind: 'load' | 'discharge';
  name: string;
  /** Date as entered (dd/mm/yyyy), shown when it cannot be parsed. */
  rawDate: string;
  time: number | null;
  state: PortStopState;
}

/** One row of the Port schedule editor; date is the date input's yyyy-mm-dd value. */
export interface PortEditRow {
  name: string;
  date: string;
}

/** Itinerary limit (heat plan page): up to 8 load and 8 discharge ports. */
export const MAX_PORTS_PER_KIND = 8;

/** Derived, display-ready facts for the voyage overview dialog. */
export interface VoyageInsights {
  progress: {
    fromPort: string;
    toPort: string;
    fromTime: number;
    toTime: number;
    pct: number;
    dayNo: number;
    totalDays: number;
    daysLeft: number;
    phase: 'before' | 'underway' | 'arrived';
  } | null;
  stops: PortStop[];
  temp: {
    loaded: number | null;
    discharge: number | null;
    /** Master log day the discharge temperature was taken from. */
    dischargeDate: string | null;
    delta: number | null;
  };
}

export interface HeatplanTimeline {
  count: number;
  rows: HeatplanTimelineRow[];
  spanStart: string | null;
  spanEnd: string | null;
  spanDays: number;
  /** Position of today on the timeline in %, or null when today is outside the span. */
  todayPct: number | null;
  activePlans: number[];
}

@Component({
  selector: 'app-all-reports',
  templateUrl: './all-reports.component.html',
  styleUrls: ['./all-reports.component.scss'],
})
export class AllReportsComponent implements OnInit {
  selectedShipLabel: string = 'Select a ship';
  /** Drives the skeleton loader for the voyage summary tiles + table. */
  isTableLoading = true;
  // Card-only loading state (so table reloads don't hide/refresh the top cards).
  isCardsLoading = true;
  filterForm!: FormGroup;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  elements: any;
  paginatedData: any[] = [];
  itemsPerPage: number = 50;
  currentPage: number = 0;
  conditionOptions = ['Ballast', 'Laden'];
  statusOptions = ['Active', 'Inactive'];
  // clientOptions = ['Eiger', 'ZeroNorth', 'Elite']; // Example options
  segmentOptions: string[] = [...CARGO_HEATING_SEGMENTS];
  filteredData: any;
  isFilterOpen: boolean = false;
  isSpecialVoyage: boolean = false;

  // Custom Multi-select states
  selectedClients: string[] = [];
  clientDropdownOpen = false;

  selectedSegments: string[] = [];
  segmentDropdownOpen = false;

  selectedCargos: string[] = [];
  cargoDropdownOpen = false;

  selectedHeatingTypes: string[] = [];
  heatingTypeDropdownOpen = false;

  selectedVessels: string[] = [];
  vesselDropdownOpen = false;
  vesselOptions: string[] = [];

  // Search queries for dropdowns
  clientSearchQuery: string = '';
  vesselSearchQuery: string = '';
  segmentSearchQuery: string = '';
  cargoSearchQuery: string = '';
  heatingTypeSearchQuery: string = '';

  underPerformanceFuel: any;
  underPerformanceTime: any;
  csvHeader: any;
  DatabaseData: any;
  graph: boolean = false;
  barChart!: Chart;
  segments: any = [];
  selectedSegment: string = 'Aframax';
  ladenChart: any;
  ballastChart: any;
  bothChart: any;
  ladenSelectedSegment: any;
  ballastSelectedSegment: any;
  bothSelectedSegment: any;
  dropdownOpen = false; // Track dropdown visibility
  selectedShips: string[] = []; // Array to store selected ship names
  shipNameSuggestions: any[] = [];
  // Dynamic segments for each graph
  ladenSegments: string[] = [];
  ballastSegments: string[] = [];
  bothSegments: string[] = [];
  underPerformanceFuelVessels: any;
  underPerformanceTimeVessels: any;
  fuelTooltipContent: any;
  timeTooltipContent: any;
  username: any;
  form: any;
  ladenData: any;
  ballastData: any;
  bothData: any;
  sortMapData: boolean = false;
  isSorting: boolean = false;

  activeVessels = 0;
  inactiveVessels = 0;
  data: any;
  voyageStatusOptions: string[] = ['Running', 'Completed']; // or use your own values
  originalData: any;
  pageSize: any;
  totalVessels: number = 0;
  completedCount: number = 0;
  runningCount: number = 0;
  notStartedCount: number = 0;
  filterNotStartedOnly = false;
  logsInsertedCount: number = 0;
  clientOptions: string[] = [];
  isNewFormLoading: boolean = false;
  specialVoyageData: any[] = [];
  cargoOptions: string[] = [];
  heatingTypeOptions: string[] = [];

  loadPortIndices: number[] = [];
  dischargePortIndices: number[] = [];
  loadPortsExpanded = false;
  dischargePortsExpanded = false;
  voyageDetailsExpanded = false;
  consumptionDetailsExpanded = false;
  averageTemperatureExpanded = false;
  isClientLogin: boolean = false;

  // Collapsed rail overall summaries (computed from the FULL filtered dataset — never from paginated rows)
  overallConsumptionCollapsedValue: string = '--';
  overallAvgTempCollapsedValue: string = '--';
  overallVoyageCollapsedValue: string = '0';
  overallLoadPortsCollapsedValue: string = '--';
  overallDischargePortsCollapsedValue: string = '--';
  loginPic: any;

  get showSubHeaderRow(): boolean {
    return false;
  }

  get headerRowSpan(): number {
    return 1;
  }

  get hasPaginatedRows(): boolean {
    return (this.paginatedData?.length ?? 0) > 0;
  }

  readonly voyageDetailColumnCount = 3;
  readonly consumptionDetailColumnCount = 6;
  readonly averageTemperatureColumnCount = 2;

  get loadPortColumnCount(): number {
    return this.loadPortIndices.length * 2;
  }

  get dischargePortColumnCount(): number {
    return this.dischargePortIndices.length * 2;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private fb: FormBuilder,
    private commonService: CargoheatingCommonService,
    private dialog: MatDialog,
    // private sharedDataService: DashboardService,
    // private requestFormService: RequestFormService
    private requestFormService: RequestFormService,
    private eRef: ElementRef,
  ) { }

  showClientFilter: boolean = false;
  searchQuery: string = '';

  private normalizePortDetails(details: any): any[] {
    if (details == null) {
      return [];
    }

    let parsed = details;
    if (typeof details === 'string') {
      try {
        parsed = JSON.parse(details);
        if (typeof parsed === 'string') {
          parsed = JSON.parse(parsed);
        }
      } catch {
        return [];
      }
    }

    if (Array.isArray(parsed)) {
      return parsed;
    }

    if (parsed && typeof parsed === 'object') {
      const indices = new Set<number>();
      Object.keys(parsed).forEach((key) => {
        const loadMatch = key.match(/^loadPort(\d+)$/i);
        const dischargeMatch = key.match(/^dischargePort(\d+)$/i);
        if (loadMatch) {
          indices.add(Number(loadMatch[1]));
        }
        if (dischargeMatch) {
          indices.add(Number(dischargeMatch[1]));
        }
      });

      if (indices.size > 0) {
        return [...indices]
          .sort((a, b) => a - b)
          .map((index) => ({
            loadPort: parsed[`loadPort${index}`] ?? '',
            departure: parsed[`departure${index}`] ?? '',
            dischargePort: parsed[`dischargePort${index}`] ?? '',
            arrival: parsed[`arrival${index}`] ?? '',
          }));
      }

      return [parsed];
    }

    return [];
  }

  private normalizeLoadPortEntry(
    entry: any,
    index: number,
  ): { loadPort: string; departure: string } {
    if (!entry || typeof entry !== 'object') {
      return { loadPort: '', departure: '' };
    }

    const suffix = index + 1;
    return {
      loadPort: String(
        entry.loadPort ?? entry[`loadPort${suffix}`] ?? '',
      ).trim(),
      departure: String(
        entry.departure ?? entry[`departure${suffix}`] ?? '',
      ).trim(),
    };
  }

  private normalizeDischargePortEntry(
    entry: any,
    index: number,
  ): { dischargePort: string; arrival: string } {
    if (!entry || typeof entry !== 'object') {
      return { dischargePort: '', arrival: '' };
    }

    const suffix = index + 1;
    return {
      dischargePort: String(
        entry.dischargePort ?? entry[`dischargePort${suffix}`] ?? '',
      ).trim(),
      arrival: String(entry.arrival ?? entry[`arrival${suffix}`] ?? '').trim(),
    };
  }

  private normalizeFleetData(fleetData: any): any | null {
    if (!fleetData) {
      return null;
    }

    return {
      ...fleetData,
      loadPortDetails: this.normalizePortDetails(fleetData.loadPortDetails).map(
        (entry, index) => this.normalizeLoadPortEntry(entry, index),
      ),
      dischargePortDetails: this.normalizePortDetails(
        fleetData.dischargePortDetails,
      ).map((entry, index) => this.normalizeDischargePortEntry(entry, index)),
    };
  }

  private hasPortData(entry: any): boolean {
    if (entry == null) {
      return false;
    }

    if (typeof entry === 'string') {
      return entry.trim().length > 0;
    }

    if (typeof entry !== 'object') {
      return false;
    }

    return Object.values(entry).some(
      (value) => value != null && String(value).trim().length > 0,
    );
  }

  private countActivePorts(details: any[] | undefined): number {
    if (!details?.length) {
      return 0;
    }

    return details.filter((entry) => this.hasPortData(entry)).length;
  }

  private toFiniteNumber(value: unknown): number | null {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const n = Number.parseFloat(String(value));
    return Number.isFinite(n) ? n : null;
  }

  private recomputeCollapsedRailOverallSummaries(): void {
    const data = (this.filteredData ?? this.originalData ?? []) as any[];

    // Consumption (MT) — use Total Heating Consumption MT as the collapsed “Consumption” value.
    let consumptionSum = 0;
    let consumptionCount = 0;
    data.forEach((row) => {
      const v = this.toFiniteNumber(
        row?.fleet_data?.totalHeatingBunkerConsumptionMT,
      );
      if (v !== null) {
        consumptionSum += v;
        consumptionCount++;
      }
    });
    this.overallConsumptionCollapsedValue =
      consumptionCount > 0 ? Math.round(consumptionSum).toLocaleString() : '--';

    // Avg Temp (°C) — represent both loaded + discharge temps by averaging per row first.
    let tempSum = 0;
    let tempCount = 0;
    data.forEach((row) => {
      const loaded = this.toFiniteNumber(
        row?.fleet_data?.averageLoadedCargoTempDegC,
      );
      const discharged = this.toFiniteNumber(
        row?.fleet_data?.averageDischCargoTempDegC,
      );

      let perRow: number | null = null;
      if (loaded !== null && discharged !== null) {
        perRow = (loaded + discharged) / 2;
      } else if (loaded !== null) {
        perRow = loaded;
      } else if (discharged !== null) {
        perRow = discharged;
      }

      if (perRow !== null) {
        tempSum += perRow;
        tempCount++;
      }
    });
    this.overallAvgTempCollapsedValue =
      tempCount > 0 ? Math.round(tempSum / tempCount).toLocaleString() : '--';

    // Voyage (RECORDS)
    this.overallVoyageCollapsedValue = (data?.length ?? 0).toLocaleString();

    // Load Port (PORTS) — display Total/Unique count
    let loadTotal = 0;
    const loadUnique = new Set<string>();
    data.forEach((row) => {
      const details = (row?.fleet_data?.loadPortDetails ?? []).filter(
        (e: any) => this.hasPortData(e),
      );
      loadTotal += details.length;
      details.forEach((e: any) => {
        const port = String(e?.loadPort ?? '').trim();
        if (port) loadUnique.add(port);
      });
    });
    this.overallLoadPortsCollapsedValue = `${loadTotal}/${loadUnique.size}`;

    // Discharge Port (PORTS) — display Total/Unique count
    let dischargeTotal = 0;
    const dischargeUnique = new Set<string>();
    data.forEach((row) => {
      const details = (row?.fleet_data?.dischargePortDetails ?? []).filter(
        (e: any) => this.hasPortData(e),
      );
      dischargeTotal += details.length;
      details.forEach((e: any) => {
        const port = String(e?.dischargePort ?? '').trim();
        if (port) dischargeUnique.add(port);
      });
    });
    this.overallDischargePortsCollapsedValue = `${dischargeTotal}/${dischargeUnique.size}`;
  }

  private normalizeVoyageStatus(status: string): string {
    const lower = status.trim().toLowerCase();

    if (lower === 'not started' || lower === 'not yet started') {
      return 'Not Started';
    }
    if (lower === 'running' || lower === 'ongoing') {
      return 'Running';
    }
    if (lower === 'completed') {
      return 'Completed';
    }

    return status.trim();
  }

  private resolveVoyageStatus(
    item: any,
    fleetData: any,
    viewMode: 'active' | 'completed',
  ): string {
    const raw = (
      item?.voyage_status ||
      fleetData?.voyageRunningOrCompleted ||
      item?.voyageRunningOrCompleted ||
      ''
    ).trim();

    if (raw) {
      return this.normalizeVoyageStatus(raw);
    }

    return viewMode === 'completed' ? 'Completed' : 'Running';
  }

  private mapRequestItem(
    item: any,
    viewMode: 'active' | 'completed' = 'active',
  ): any {
    const fleetData = this.normalizeFleetData(item.fleet_data || null);
    const mapped: any = {
      vessel_name: item.vessel_name || '',
      vessel_imo: Number(item.vessel_imo),
      voyage_no: String(item.voyage_no),
      cargo_type: item.cargo_type || '',
      segment: item.segment || '',
      client: item.client || '',
      voyage_id: item.voyage_id,
      pic: item.pic || fleetData?.pic || '',
      form_timing_display:
        item.form_timing_display || fleetData?.dateReceivedFromClient || '',
      heating_type: item.heating_type || fleetData?.heatingCoilsOrDeckHeatExchanger || '',
      voyage_status: this.resolveVoyageStatus(item, fleetData, viewMode),
      fleet_data: fleetData,
      heatplan_count: Number(item.heatplan_count ?? item.heatplans?.length ?? 0),
      heatplans: Array.isArray(item.heatplans) ? item.heatplans : [],
    };
    // Heat plan due alerts apply to active voyages only
    mapped.hp_due =
      viewMode === 'completed' || this.isCompletedVessel(mapped)
        ? null
        : this.computeHeatplanDue(mapped);
    return mapped;
  }

  private mapRequestsForView(
    response: any[],
    viewMode: 'active' | 'completed',
  ): any[] {
    const mapped = (response || [])
      .map((item: any) => this.mapRequestItem(item, viewMode))
      .sort((a: any, b: any) => this.compareDefault(a, b, viewMode === 'completed'));

    if (viewMode === 'completed') {
      return mapped.filter((item) => this.isCompletedVessel(item));
    }

    return mapped.filter((item) => !this.isCompletedVessel(item));
  }

  getVoyageStatus(item: any): string {
    return (item?.voyage_status || '').trim();
  }

  isCompletedVessel(item: any): boolean {
    return this.getVoyageStatus(item).toLowerCase() === 'completed';
  }

  isRunningVessel(item: any): boolean {
    return this.getVoyageStatus(item).toLowerCase() === 'running';
  }

  isNotStartedVessel(item: any): boolean {
    return this.getVoyageStatus(item).toLowerCase() === 'not started';
  }

  getVoyageStatusClass(item: any): string {
    if (this.isCompletedVessel(item)) {
      return 'status-completed';
    }
    if (this.isRunningVessel(item)) {
      return 'status-running';
    }
    if (this.isNotStartedVessel(item)) {
      return 'status-not-started';
    }
    return 'status-unknown';
  }

  getVoyageRowClass(item: any): string {
    if (this.isSpecialVoyage) {
      return 'completed-row';
    }
    const dueLevel = item?.hp_due?.level;
    if (dueLevel === 'overdue' || dueLevel === 'today') {
      return 'hp-alert-row';
    }
    if (this.isNotStartedVessel(item)) {
      return 'not-started-row';
    }
    if (this.isRunningVessel(item)) {
      return 'running-row';
    }
    return '';
  }

  toggleLoadPorts(event: Event): void {
    event.stopPropagation();
    this.loadPortsExpanded = !this.loadPortsExpanded;
  }

  toggleDischargePorts(event: Event): void {
    event.stopPropagation();
    this.dischargePortsExpanded = !this.dischargePortsExpanded;
  }

  toggleVoyageDetails(event: Event): void {
    event.stopPropagation();
    this.voyageDetailsExpanded = !this.voyageDetailsExpanded;
  }

  toggleConsumptionDetails(event: Event): void {
    event.stopPropagation();
    this.consumptionDetailsExpanded = !this.consumptionDetailsExpanded;
  }

  toggleAverageTemperature(event: Event): void {
    event.stopPropagation();
    this.averageTemperatureExpanded = !this.averageTemperatureExpanded;
  }

  get allDetailSectionsExpanded(): boolean {
    return (
      this.voyageDetailsExpanded &&
      this.loadPortsExpanded &&
      this.dischargePortsExpanded &&
      this.consumptionDetailsExpanded &&
      this.averageTemperatureExpanded
    );
  }

  activeCategoryView:
    | 'overview'
    | 'consumption'
    | 'temp'
    | 'voyage'
    | 'ports'
    | 'all' = 'overview';
  expandedRowKey: any = null;
  modalElement: any = null;
  copiedTile: string | null = null;
  isCopyingTile = false;
  isNavigatingRow = false;
  navigatingVesselName = '';
  /** Total cargo and voyage duration chips of the overview dialog (from get_combined_voyage_data). */
  totalCargoQty: number | null = null;
  totalDaysOfVoyage: number | null = null;
  isLoadingVoyageTotals = false;
  /** Heat plans of the voyage in the overview dialog, laid out as a timeline (active voyages only). */
  modalHeatplans: HeatplanTimeline | null = null;
  modalInsights: VoyageInsights | null = null;
  /** Master vs heat plan consumption of the voyage in the overview dialog (from the master and plan logs). */
  modalConsumption: VoyageConsumptionSummary | null = null;
  isLoadingModalConsumption = false;
  /** Set when the master logs could not be loaded (nothing to show) or only the heat plan logs failed. */
  modalConsumptionError: 'master' | 'plan' | null = null;
  private modalConsumptionSub: Subscription | null = null;
  /** Keeps the dialog inside the visible content area (below the app header, right of the sidebar). */
  modalInset = { top: 0, left: 0 };

  /** Port schedule editor of the overview dialog; null while the tile shows the route. */
  portEdit: { load: PortEditRow[]; discharge: PortEditRow[] } | null = null;
  isSavingPorts = false;
  readonly maxPortsPerKind = MAX_PORTS_PER_KIND;
  readonly portEditKinds = [
    { key: 'load', label: 'load', dateLabel: 'Departure' },
    { key: 'discharge', label: 'discharge', dateLabel: 'Arrival' },
  ] as const;

  /** Heating instructions being edited in the overview dialog; null while not editing. */
  instructionsDraft: string | null = null;
  isSavingInstructions = false;

  openDetailsModal(element: any, event?: Event): void {
    event?.stopPropagation();
    this.portEdit = null;
    this.instructionsDraft = null;
    this.modalElement = element;
    this.modalHeatplans = this.isCompletedVessel(element)
      ? null
      : this.buildHeatplanTimeline(element);
    this.modalInsights = this.buildVoyageInsights(element);
    this.updateModalInset();
    this.loadModalConsumption(element);
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.instructionsDraft !== null) {
      this.cancelInstructionsEdit();
    } else if (this.portEdit) {
      this.cancelPortEdit();
    } else if (this.modalElement) {
      this.closeDetailsModal();
    }
  }

  closeDetailsModal(): void {
    this.portEdit = null;
    this.instructionsDraft = null;
    this.modalElement = null;
    this.modalHeatplans = null;
    this.modalInsights = null;
    this.resetModalConsumption();
  }

  // ------------------------------------------------------------------ overview port schedule editor

  /** Shown on the locked Edit buttons and toasted when one is clicked. */
  readonly overviewEditLockedMessage = 'Action not allowed for completed voyage.';

  /** Ports and heating instructions can be changed from the overview only while the voyage is active. */
  get canEditOverview(): boolean {
    return !!this.modalElement && !this.isSpecialVoyage && !this.isCompletedVessel(this.modalElement);
  }

  /** False (after telling the user why) when the dialog's voyage is completed. */
  private ensureOverviewEditable(): boolean {
    if (!this.canEditOverview) {
      this.toastr.info(this.overviewEditLockedMessage);
      return false;
    }
    return true;
  }

  /** Prefills the editor from the voyage's ports; an empty list starts with one blank row, as on the itinerary. */
  startPortEdit(event?: Event): void {
    event?.stopPropagation();
    if (!this.ensureOverviewEditable()) {
      return;
    }
    const fleet = this.modalElement?.fleet_data || {};
    const portCalls: any[] = Array.isArray(fleet.portDetails) ? fleet.portDetails : [];
    // Like the itinerary: a port without a date falls back to its port call (COSP for load, EOSP for discharge)
    const portCallDate = (name: string, type: 'load' | 'discharge') => {
      const call = portCalls.find(
        (p: any) => p?.type === type && p?.name?.trim().toLowerCase() === name.trim().toLowerCase(),
      );
      return type === 'load' ? call?.cosp : call?.eosp;
    };
    const toRows = (details: any[], nameKey: string, dateKey: string, type: 'load' | 'discharge'): PortEditRow[] => {
      const rows = (details || []).map((p: any) => {
        const name = String(p?.[nameKey] ?? '').trim();
        return { name, date: this.toInputDate(p?.[dateKey] || (name ? portCallDate(name, type) : '')) };
      });
      return rows.length ? rows : [{ name: '', date: '' }];
    };
    this.portEdit = {
      load: toRows(fleet.loadPortDetails, 'loadPort', 'departure', 'load'),
      discharge: toRows(fleet.dischargePortDetails, 'dischargePort', 'arrival', 'discharge'),
    };
  }

  cancelPortEdit(): void {
    if (!this.isSavingPorts) {
      this.portEdit = null;
    }
  }

  addPortRow(kind: 'load' | 'discharge'): void {
    const rows = this.portEdit?.[kind];
    if (rows && rows.length < this.maxPortsPerKind) {
      rows.push({ name: '', date: '' });
    }
  }

  removePortRow(kind: 'load' | 'discharge', index: number): void {
    const rows = this.portEdit?.[kind];
    if (!rows) {
      return;
    }
    rows.splice(index, 1);
    if (!rows.length) {
      rows.push({ name: '', date: '' });
    }
  }

  /** A dated row needs a port name; a name without a date is allowed, as on the itinerary. */
  isPortRowInvalid(row: PortEditRow): boolean {
    return !!row.date && !row.name.trim();
  }

  get hasInvalidPortRows(): boolean {
    return !!this.portEdit && [...this.portEdit.load, ...this.portEdit.discharge].some((r) => this.isPortRowInvalid(r));
  }

  /** Saves the load and discharge ports through the itinerary API (fleet/post_data), in the itinerary's format. */
  savePortEdit(): void {
    const element = this.modalElement;
    const edit = this.portEdit;
    const voyageId = element?.voyage_id;
    if (!edit || this.isSavingPorts || !this.ensureOverviewEditable()) {
      return;
    }
    if (voyageId === undefined || voyageId === null || voyageId === '') {
      this.toastr.warning('Voyage ID not available');
      return;
    }
    if (this.hasInvalidPortRows) {
      this.toastr.warning('Enter a port name for every row with a date');
      return;
    }

    const filled = (rows: PortEditRow[]) => rows.filter((r) => r.name.trim() || r.date);
    const loadPortDetails = filled(edit.load).map((r) => ({
      loadPort: r.name.trim(),
      departure: this.toDDMMYYYY(r.date),
    }));
    const dischargePortDetails = filled(edit.discharge).map((r) => ({
      dischargePort: r.name.trim(),
      arrival: this.toDDMMYYYY(r.date),
    }));

    this.isSavingPorts = true;
    // post_data merges the keys it receives into fleet_data, so only the ports are sent
    this.commonService
      .postFleetData({ voyage_id: voyageId, loadPortDetails, dischargePortDetails })
      .subscribe({
        next: () => {
          this.isSavingPorts = false;
          this.portEdit = null;
          this.applySavedPorts(element, loadPortDetails, dischargePortDetails);
          this.toastr.success('Port schedule saved');
        },
        error: () => {
          this.isSavingPorts = false;
          this.toastr.error('Failed to save port schedule');
        },
      });
  }

  /** Shows the saved ports in the dialog and the voyage list without reloading. */
  private applySavedPorts(element: any, loadPortDetails: any[], dischargePortDetails: any[]): void {
    element.fleet_data = this.normalizeFleetData({
      ...(element.fleet_data || {}),
      loadPortDetails,
      dischargePortDetails,
    });
    element.hp_due =
      this.isSpecialVoyage || this.isCompletedVessel(element) ? null : this.computeHeatplanDue(element);
    if (this.modalElement === element) {
      const temp = this.modalInsights?.temp;
      const insights = this.buildVoyageInsights(element);
      this.modalInsights = temp ? { ...insights, temp } : insights;
    }
  }

  // ------------------------------------------------------------------ overview heating instructions editor

  startInstructionsEdit(event?: Event): void {
    event?.stopPropagation();
    if (!this.ensureOverviewEditable()) {
      return;
    }
    this.instructionsDraft = this.modalElement?.fleet_data?.heatingInstructions || '';
  }

  cancelInstructionsEdit(): void {
    if (!this.isSavingInstructions) {
      this.instructionsDraft = null;
    }
  }

  /** Saves through the itinerary API (fleet/post_data), which merges the keys it receives into fleet_data. */
  saveInstructionsEdit(): void {
    const element = this.modalElement;
    const voyageId = element?.voyage_id;
    if (this.instructionsDraft === null || this.isSavingInstructions || !this.ensureOverviewEditable()) {
      return;
    }
    if (voyageId === undefined || voyageId === null || voyageId === '') {
      this.toastr.warning('Voyage ID not available');
      return;
    }

    const heatingInstructions = this.instructionsDraft.trim();
    this.isSavingInstructions = true;
    this.commonService.postFleetData({ voyage_id: voyageId, heatingInstructions }).subscribe({
      next: () => {
        this.isSavingInstructions = false;
        this.instructionsDraft = null;
        element.fleet_data = this.normalizeFleetData({ ...(element.fleet_data || {}), heatingInstructions });
        this.toastr.success('Heating instructions saved');
      },
      error: () => {
        this.isSavingInstructions = false;
        this.toastr.error('Failed to save heating instructions');
      },
    });
  }

  /** Any stored date (dd/mm/yyyy, yyyy-mm-dd, ISO) → the date input's yyyy-mm-dd, or ''. */
  private toInputDate(value: any): string {
    const time = this.parseFlexibleDate(value);
    if (time === null) {
      return '';
    }
    const d = new Date(time);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  /** yyyy-mm-dd → dd/mm/yyyy, the itinerary's stored port date format. */
  private toDDMMYYYY(value: string): string {
    const iso = this.toInputDate(value);
    if (!iso) {
      return '';
    }
    const [year, month, day] = iso.split('-');
    return `${day}/${month}/${year}`;
  }

  // ------------------------------------------------------------------ overview consumption & temperatures

  /**
   * Consumption and cargo temperatures for the overview dialog, from the same APIs as the voyage
   * monitoring and comparison pages: the tank profile and master logs (get_combined_voyage_data) and
   * every heat plan's daily logs (get_logs_of_heatplan). Plan numbers come from the voyage list row;
   * get_available_heatplan_details is only called when the row has none. The total cargo and voyage
   * duration chips also come from get_combined_voyage_data, as soon as it arrives.
   */
  private loadModalConsumption(element: any): void {
    this.resetModalConsumption();
    const voyageId = element?.voyage_id;
    if (voyageId === undefined || voyageId === null || voyageId === '') {
      return;
    }
    const id = String(voyageId);
    const isCompleted = this.isCompletedVessel(element);
    this.isLoadingModalConsumption = true;
    this.isLoadingVoyageTotals = true;

    const combined$: Observable<any | null> = this.commonService.getCombinedVoyageData(id).pipe(
      catchError(() => of(null)),
      tap((combined) => this.applyVoyageTotals(combined)),
    );

    const rowPlanNumbers = this.planNumbersOf(element?.heatplans);
    const planNumbers$: Observable<number[]> = rowPlanNumbers.length
      ? of(rowPlanNumbers)
      : this.commonService.getAvailableHeatplanDetails(id).pipe(
        map((body) => this.planNumbersOf((body?.response ?? body)?.heatplans)),
      );

    const planRows$: Observable<HeatplanConsumptionRow[] | null> = planNumbers$.pipe(
      switchMap((numbers) =>
        numbers.length
          ? forkJoin(
            numbers.map((num) =>
              this.commonService
                .getLogsOfHeatplan(id, num)
                .pipe(map((body) => extractHeatplanLogs<HeatplanConsumptionRow>(body))),
            ),
          )
          : of([] as HeatplanConsumptionRow[][]),
      ),
      // Ascending plan order, so a later plan overwrites the days it shares with the previous one
      map((planLogsList) => mergeHeatplanLogsByDate(planLogsList)),
      catchError(() => of(null)),
    );

    this.modalConsumptionSub = forkJoin([combined$, planRows$]).subscribe(([combined, planRows]) => {
      this.isLoadingModalConsumption = false;
      if (!combined) {
        this.modalConsumptionError = 'master';
        return;
      }
      const masterRows = extractMasterVoyageLogs<MasterConsumptionRow & MasterTempRow>(combined);
      this.modalConsumptionError = planRows ? null : 'plan';
      this.modalConsumption = summariseVoyageConsumption(planRows ?? [], masterRows);
      this.applyModalTemps(masterRows, extractTankProfile(combined), isCompleted);
    });
  }

  /**
   * Total cargo: the tank profile's cargo quantities summed (0 shows "Cargo yet to load").
   * Voyage duration: the number of distinct master log days; hidden while there are none.
   */
  private applyVoyageTotals(combined: any | null): void {
    this.isLoadingVoyageTotals = false;
    if (!combined) {
      return;
    }
    this.totalCargoQty = totalCargoQuantity(extractTankProfile(combined));
    const logDates = new Set(
      extractMasterVoyageLogs<MasterTempRow>(combined)
        .map((row) => row?.record_date)
        .filter(Boolean),
    );
    this.totalDaysOfVoyage = logDates.size || null;
  }

  /**
   * Avg loaded: tank load temps weighted by cargo quantity (as on the monitoring page).
   * Avg discharge (completed voyages only): the last master log day's tank temps, weighted the same way.
   */
  private applyModalTemps(
    masterRows: MasterTempRow[],
    profile: TankProfileTempFields,
    isCompleted: boolean,
  ): void {
    if (!this.modalInsights) {
      return;
    }
    const loaded = averageLoadTemp(profile);
    const lastDay = isCompleted ? lastDayCargoTemp(masterRows, profile) : { temp: null, date: null };
    this.modalInsights = {
      ...this.modalInsights,
      temp: {
        loaded,
        discharge: lastDay.temp,
        dischargeDate: lastDay.date,
        delta: loaded !== null && lastDay.temp !== null ? lastDay.temp - loaded : null,
      },
    };
  }

  private planNumbersOf(heatplans: any): number[] {
    return (Array.isArray(heatplans) ? heatplans : [])
      .map((plan: any) => Number(plan?.plan_number))
      .filter((num: number) => Number.isFinite(num) && num > 0)
      .sort((a: number, b: number) => a - b);
  }

  private resetModalConsumption(): void {
    this.modalConsumptionSub?.unsubscribe();
    this.modalConsumptionSub = null;
    this.modalConsumption = null;
    this.isLoadingModalConsumption = false;
    this.modalConsumptionError = null;
    this.totalCargoQty = null;
    this.totalDaysOfVoyage = null;
    this.isLoadingVoyageTotals = false;
  }

  /** "12.3 MT" for the overview consumption tile and its copy text. */
  formatConsumptionMt(value: number | null | undefined): string {
    return value == null || !Number.isFinite(value) ? '—' : `${value.toFixed(1)} MT`;
  }

  // ------------------------------------------------------------------ heat plan due status

  /**
   * Today's IST calendar date from the browser clock, as local midnight so it compares directly with
   * the plan and port dates (which are parsed as local midnight).
   */
  private startOfToday(): number {
    const ist = new Date(Date.now() + IST_OFFSET_MS);
    return new Date(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate()).getTime();
  }

  /** Due date = latest heat plan's last day minus HEATPLAN_DUE_LEAD_DAYS, counted from today. */
  computeHeatplanDue(item: any): HeatplanDue {
    const dayMs = 24 * 60 * 60 * 1000;
    const toTime = (value: string) => new Date(`${value}T00:00:00`).getTime();
    const plans = (item?.heatplans || []).filter(
      (p: any) => p?.end_date && Number.isFinite(toTime(p.end_date)),
    );

    if (!plans.length) {
      const running = this.isRunningVessel(item);
      return {
        level: running ? 'missing' : 'none',
        daysLeft: null,
        dueTime: null,
        latestPlanNumber: null,
        latestPlanEnd: null,
        label: running ? 'Monitoring not started' : 'Monitoring not started',
        port: null,
      };
    }

    // Latest plan = furthest end date (ties go to the higher plan number)
    const latest = plans.reduce((a: any, b: any) => {
      const diff = toTime(b.end_date) - toTime(a.end_date);
      return diff > 0 || (diff === 0 && Number(b.plan_number) > Number(a.plan_number)) ? b : a;
    });

    // No plan is sent for port days: on a discharge arrival day (or the day before) show the port
    // call instead of a due. Any other day shows the due.
    const today = this.startOfToday();
    const port = this.findDischargePortAroundToday(item, today);
    if (port) {
      return {
        level: 'port',
        daysLeft: null,
        dueTime: null,
        latestPlanNumber: Number(latest.plan_number) || null,
        latestPlanEnd: latest.end_date,
        label: `${port.arriving ? 'Arriving' : 'In port'} · ${port.name}`,
        port,
      };
    }

    const dueTime = toTime(latest.end_date) - HEATPLAN_DUE_LEAD_DAYS * dayMs;
    const daysLeft = Math.round((dueTime - today) / dayMs);

    let level: HeatplanDueLevel;
    let label: string;
    if (daysLeft < 0) {
      level = 'overdue';
      label = `Overdue by ${-daysLeft} ${daysLeft === -1 ? 'day' : 'days'}`;
    } else if (daysLeft === 0) {
      level = 'today';
      label = 'Due today';
    } else {
      level = daysLeft <= HEATPLAN_DUE_SOON_DAYS ? 'soon' : 'upcoming';
      label = daysLeft === 1 ? 'Due tomorrow' : `Due in ${daysLeft} days`;
    }

    return {
      level,
      daysLeft,
      dueTime,
      latestPlanNumber: Number(latest.plan_number) || null,
      latestPlanEnd: latest.end_date,
      label,
      port: null,
    };
  }

  /**
   * Discharge port arriving today (in port) or tomorrow (arriving), by today's IST date.
   * In port wins over arriving.
   */
  private findDischargePortAroundToday(item: any, today: number): HeatplanDue['port'] {
    const dayMs = 24 * 60 * 60 * 1000;
    const matches = (item?.fleet_data?.dischargePortDetails || [])
      .map((p: any) => ({ name: String(p?.dischargePort || '').trim(), arrival: this.parseDisplayDate(p?.arrival || '') }))
      .filter((p: { name: string; arrival: number | null }) => p.name && p.arrival !== null)
      .map((p: { name: string; arrival: number }) => ({ ...p, gap: Math.round((p.arrival - today) / dayMs) }))
      .filter((p: { gap: number }) => p.gap === 0 || p.gap === 1)
      .sort((a: { gap: number }, b: { gap: number }) => a.gap - b.gap);
    return matches.length
      ? { name: matches[0].name, arrival: matches[0].arrival, arriving: matches[0].gap === 1 }
      : null;
  }

  /** Hover text of the "Next heat plan" status. */
  heatplanDueTitle(due: HeatplanDue): string {
    if (due.level === 'port' && due.port) {
      const where = due.port.arriving
        ? `Arriving at ${due.port.name} tomorrow (${this.formatEpochDate(due.port.arrival)})`
        : `In port at ${due.port.name} (arrived ${this.formatEpochDate(due.port.arrival)})`;
      return `${where}. Heat plans are not sent while the vessel is in port.`;
    }
    return due.dueTime !== null
      ? `Latest plan HP ${due.latestPlanNumber} ends ${this.formatHeatplanDate(due.latestPlanEnd)} · next plan due 2 days earlier`
      : due.label;
  }

  /** The vessel is arriving at / in a discharge port today: shown on the latest plan's row instead of its state. */
  portOnLatestPlan(plan: { plan_number: number }): HeatplanDue['port'] {
    const due: HeatplanDue | null = this.modalElement?.hp_due;
    return due?.level === 'port' && due.latestPlanNumber === plan.plan_number ? due.port : null;
  }

  /**
   * Sort rank for the "send by" date, lower = first: dues from today onward, soonest first
   * (0, 1, 2 …), then overdue ones, least overdue first. Voyages with no heat plan have no rank.
   */
  private heatplanDueRank(item: any): number | null {
    const daysLeft: number | null | undefined = item?.hp_due?.daysLeft;
    if (daysLeft === null || daysLeft === undefined) {
      return null;
    }
    return daysLeft >= 0 ? daysLeft : 100000 - daysLeft;
  }

  /**
   * Group for the active voyages' default order, lower = first: heat plan due, arriving at port,
   * in port, monitoring not started (running), monitoring not started (not started).
   */
  private nextPlanGroup(item: any): number {
    const due: HeatplanDue | null = item?.hp_due;
    switch (due?.level) {
      case 'overdue':
      case 'today':
      case 'soon':
      case 'upcoming':
        return 0;
      case 'port':
        return due.port?.arriving ? 1 : 2;
      case 'missing':
        return 3;
      case 'none':
        return 4;
      default:
        return 5;
    }
  }

  /**
   * Default order for active voyages: by next plan group, then most overdue first followed by
   * sooner dues, then latest discharge date.
   */
  private compareByNextPlanDue(a: any, b: any): number {
    const groupDiff = this.nextPlanGroup(a) - this.nextPlanGroup(b);
    if (groupDiff !== 0) {
      return groupDiff;
    }
    const daysA: number | null | undefined = a?.hp_due?.daysLeft;
    const daysB: number | null | undefined = b?.hp_due?.daysLeft;
    if (daysA != null && daysB != null && daysA !== daysB) {
      return daysA - daysB;
    }
    return this.compareByLatestDischarge(a, b);
  }

  /** Short date ("24 Oct"); the year is added only when it is not the current one. */
  formatDueDate(value: number | string | null): string {
    if (value === null || value === '') {
      return '—';
    }
    const date = typeof value === 'number' ? new Date(value) : new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    const sameYear = date.getFullYear() === new Date().getFullYear();
    return date.toLocaleDateString('en-GB', sameYear
      ? { day: '2-digit', month: 'short' }
      : { day: '2-digit', month: 'short', year: 'numeric' });
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.modalElement) {
      this.updateModalInset();
    }
  }

  /**
   * The app header and sidebar are drawn above this page, so a full-viewport overlay hides the
   * dialog's top behind them. Pin the overlay to the area this component actually occupies.
   */
  private updateModalInset(): void {
    const rect = (this.eRef.nativeElement as HTMLElement).getBoundingClientRect();
    this.modalInset = {
      top: Math.max(0, Math.round(rect.top)),
      left: Math.max(0, Math.round(rect.left)),
    };
  }

  /** Parses dd/mm/yyyy (or yyyy-mm-dd); returns null for anything implausible such as year 0026. */
  private parseDisplayDate(value: string): number | null {
    if (!value) {
      return null;
    }
    const text = String(value).trim();
    let year: number, month: number, day: number;
    const dmy = text.match(/^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})/);
    const ymd = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (dmy) {
      [day, month, year] = [Number(dmy[1]), Number(dmy[2]), Number(dmy[3])];
    } else if (ymd) {
      [year, month, day] = [Number(ymd[1]), Number(ymd[2]), Number(ymd[3])];
    } else {
      return null;
    }
    if (year < 2000 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) {
      return null;
    }
    const date = new Date(year, month - 1, day);
    return date.getMonth() === month - 1 ? date.getTime() : null;
  }

  private toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }

  private buildVoyageInsights(element: any): VoyageInsights {
    const fleet = element?.fleet_data || {};
    const dayMs = 24 * 60 * 60 * 1000;
    const today = this.startOfToday();

    const stops: PortStop[] = [
      ...(fleet.loadPortDetails || []).map((p: any) => ({
        kind: 'load' as const,
        name: p?.loadPort || '—',
        rawDate: p?.departure || '',
      })),
      ...(fleet.dischargePortDetails || []).map((p: any) => ({
        kind: 'discharge' as const,
        name: p?.dischargePort || '—',
        rawDate: p?.arrival || '',
      })),
    ].map((stop) => ({ ...stop, time: this.parseDisplayDate(stop.rawDate), state: 'upcoming' as PortStopState }));

    // A stop is done once its date has passed; the first stop still ahead is "next".
    let nextMarked = false;
    stops.forEach((stop) => {
      if (stop.time !== null && stop.time < today) {
        stop.state = 'done';
      } else if (!nextMarked && stop.time !== null) {
        stop.state = 'next';
        nextMarked = true;
      }
    });

    // Progress: first load departure → last discharge arrival
    const loads = stops.filter((s) => s.kind === 'load' && s.time !== null);
    const discharges = stops.filter((s) => s.kind === 'discharge' && s.time !== null);
    let progress: VoyageInsights['progress'] = null;
    if (loads.length && discharges.length) {
      const from = loads.reduce((a, b) => (b.time! < a.time! ? b : a));
      const to = discharges.reduce((a, b) => (b.time! > a.time! ? b : a));
      if (to.time! > from.time!) {
        const totalDays = Math.round((to.time! - from.time!) / dayMs);
        const elapsed = Math.round((today - from.time!) / dayMs);
        progress = {
          fromPort: from.name,
          toPort: to.name,
          fromTime: from.time!,
          toTime: to.time!,
          pct: Math.max(0, Math.min(100, (elapsed / totalDays) * 100)),
          dayNo: Math.max(0, Math.min(totalDays, elapsed)),
          totalDays,
          daysLeft: Math.max(0, totalDays - elapsed),
          phase: elapsed < 0 ? 'before' : elapsed >= totalDays ? 'arrived' : 'underway',
        };
      }
    }

    return {
      progress,
      stops,
      // Filled from the tank profile and master logs once they load (applyModalTemps)
      temp: { loaded: null, discharge: null, dischargeDate: null, delta: null },
    };
  }

  formatEpochDate(time: number | null): string {
    return time === null
      ? '—'
      : new Date(time).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  private buildHeatplanTimeline(element: any): HeatplanTimeline {
    const dayMs = 24 * 60 * 60 * 1000;
    const toTime = (value: string) => new Date(`${value}T00:00:00`).getTime();
    const plans: VoyageHeatplan[] = (element?.heatplans || [])
      .filter((p: any) => p?.start_date && p?.end_date && Number.isFinite(toTime(p.start_date)) && Number.isFinite(toTime(p.end_date)))
      .map((p: any) => ({
        plan_number: Number(p.plan_number),
        start_date: p.start_date,
        end_date: p.end_date,
        days: Number(p.days) || Math.round((toTime(p.end_date) - toTime(p.start_date)) / dayMs) + 1,
      }))
      .sort((a: VoyageHeatplan, b: VoyageHeatplan) => a.plan_number - b.plan_number);

    const count = Number(element?.heatplan_count ?? plans.length) || plans.length;
    if (!plans.length) {
      return { count, rows: [], spanStart: null, spanEnd: null, spanDays: 0, todayPct: null, activePlans: [] };
    }

    const spanStart = plans.map((p) => p.start_date).sort()[0];
    const spanEnd = plans.map((p) => p.end_date).sort().reverse()[0];
    const min = toTime(spanStart);
    // Treat each end date as inclusive, so a plan's bar covers its last day.
    const max = toTime(spanEnd) + dayMs;
    const range = Math.max(max - min, dayMs);

    const today = this.startOfToday();
    // Plans overlap (7 days each, a new one every 5th day): a newer plan supersedes the earlier ones from its
    // start date, so the earlier plan is completed that day even though its own end date is still ahead.
    const stateOf = (p: VoyageHeatplan, index: number): HeatplanState => {
      const superseded = plans.slice(index + 1).some((later) => toTime(later.start_date) <= today);
      if (toTime(p.end_date) < today || superseded) return 'done';
      if (toTime(p.start_date) > today) return 'upcoming';
      return 'active';
    };

    const rows: HeatplanTimelineRow[] = plans.map((p, index) => {
      const left = ((toTime(p.start_date) - min) / range) * 100;
      const width = Math.max(((toTime(p.end_date) + dayMs - toTime(p.start_date)) / range) * 100, 2);
      return { ...p, state: stateOf(p, index), left: Math.min(left, 100 - width), width };
    });

    return {
      count,
      rows,
      spanStart,
      spanEnd,
      spanDays: Math.round((max - min) / dayMs),
      todayPct: today >= min && today < max ? ((today + dayMs / 2 - min) / range) * 100 : null,
      activePlans: rows.filter((r) => r.state === 'active').map((r) => r.plan_number),
    };
  }

  formatHeatplanDate(value: string | null): string {
    if (!value) {
      return '—';
    }
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  toggleRowExpand(element: any, event?: Event): void {
    event?.stopPropagation();
    if (this.modalElement === element) {
      this.closeDetailsModal();
    } else {
      this.openDetailsModal(element, event);
    }
  }

  isRowExpanded(element: any): boolean {
    return this.modalElement === element;
  }

  async copyTileData(
    tileType: 'consumption' | 'temp' | 'voyage' | 'ports' | 'heatplans',
    event: Event,
  ): Promise<void> {
    event.stopPropagation();
    if (!this.modalElement) return;

    const elem = this.modalElement;
    const vesselHeader = `Vessel: ${elem.vessel_name || '—'} (IMO: ${elem.vessel_imo || '—'}) | Status: ${this.getVoyageStatus(elem)}`;
    let title = '';
    let textContent = '';
    let htmlContent = '';

    if (tileType === 'consumption') {
      title = 'Fuel & Heating Consumption';
      const ae = elem.fleet_data?.aeConsumptionForBoilerOperationMT || '—';
      const deck = elem.fleet_data?.deckHeatExchangerBunkerConsumptionMT || '—';
      const totalHeating =
        elem.fleet_data?.totalHeatingBunkerConsumptionMT || '—';
      const mc = this.modalConsumption;
      const fmt = (value: number | null | undefined) => this.formatConsumptionMt(value);
      const totalBunker = fmt(mc?.masterTotalMt);
      const totalBunkerSplit = mc?.masterSplit
        ? ` (Raise ${fmt(mc.masterSplit.raise)} · Maintain ${fmt(mc.masterSplit.maintain)})`
        : '';
      const masterPlanDays = mc?.planDays
        ? `${fmt(mc.masterPlanDaysMt)}${mc.masterPlanDaysSplit
          ? ` (Raise ${fmt(mc.masterPlanDaysSplit.raise)} · Maintain ${fmt(mc.masterPlanDaysSplit.maintain)})`
          : ''} · ${mc.masterReportedPlanDays} of ${mc.planDays} plan days reported`
        : '—';
      const estPlan = mc?.planDays ? fmt(mc.planTillLastReportMt) : '—';
      const estPlanSplit = mc?.planDays
        ? ` (Raise ${fmt(mc.planSplit.raise)} · Maintain ${fmt(mc.planSplit.maintain)})`
        : '';

      textContent = `🚢 ${vesselHeader}\n🔥 Fuel & Heating Consumption:\n• AE Cons.: ${ae} MT\n• Deck Heat Exch.: ${deck} MT\n• Total Bunker: ${totalBunker}${totalBunkerSplit}\n• Master · Heat Plan Days: ${masterPlanDays}\n• Total Heating: ${totalHeating} MT\n• Est. Plan (to last master report): ${estPlan}${estPlanSplit}`;
      htmlContent = `<div><strong>🚢 ${vesselHeader}</strong><br/><strong>🔥 Fuel & Heating Consumption:</strong><ul><li><strong>AE Cons.:</strong> ${ae} MT</li><li><strong>Deck Heat Exch.:</strong> ${deck} MT</li><li><strong>Total Bunker:</strong> ${totalBunker}${totalBunkerSplit}</li><li><strong>Master · Heat Plan Days:</strong> ${masterPlanDays}</li><li><strong>Total Heating:</strong> ${totalHeating} MT</li><li><strong>Est. Plan (to last master report):</strong> ${estPlan}${estPlanSplit}</li></ul></div>`;
    } else if (tileType === 'temp') {
      title = 'Cargo Temperatures';
      const temp = this.modalInsights?.temp;
      const fmtTemp = (value: number | null | undefined) =>
        value == null ? '—' : `${value.toFixed(1)} °C`;
      const avgLoad = fmtTemp(temp?.loaded);
      const avgDisch = fmtTemp(temp?.discharge);

      textContent = `🚢 ${vesselHeader}\n🌡️ Cargo Temperatures:\n• Avg Loaded Temp: ${avgLoad}\n• Avg Discharge Temp: ${avgDisch}`;
      htmlContent = `<div><strong>🚢 ${vesselHeader}</strong><br/><strong>🌡️ Cargo Temperatures:</strong><ul><li><strong>Avg Loaded Temp:</strong> ${avgLoad}</li><li><strong>Avg Discharge Temp:</strong> ${avgDisch}</li></ul></div>`;
    } else if (tileType === 'voyage') {
      title = 'Voyage Particulars';
      const voyNo = elem.voyage_no || '—';
      const rec = elem.form_timing_display || '—';
      const heatInst = elem.fleet_data?.heatingInstructions || '—';

      textContent = `🚢 ${vesselHeader}\n🗺️ Voyage Details:\n• Voyage No.: ${voyNo}\n• Vessel Received: ${rec}\n• Heating Instructions: ${heatInst}`;
      htmlContent = `<div><strong>🚢 ${vesselHeader}</strong><br/><strong>🗺️ Voyage Details:</strong><ul><li><strong>Voyage No.:</strong> ${voyNo}</li><li><strong>Vessel Received:</strong> ${rec}</li><li><strong>Heating Instructions:</strong> ${heatInst}</li></ul></div>`;
    } else if (tileType === 'ports') {
      title = 'Port Schedule';
      const loadPorts: any[] = elem.fleet_data?.loadPortDetails || [];
      const dischPorts: any[] = elem.fleet_data?.dischargePortDetails || [];

      textContent = `🚢 ${vesselHeader}\n⚓ Port Schedule:\n`;
      htmlContent = `<div><strong>🚢 ${vesselHeader}</strong><br/><strong>⚓ Port Schedule:</strong><br/>`;

      if (loadPorts.length) {
        textContent += `• Load Ports (${loadPorts.length}):\n`;
        htmlContent += `<strong>Load Ports (${loadPorts.length}):</strong><ul>`;
        loadPorts.forEach((lp, idx) => {
          const dt = lp.departure
            ? ` (Departure: ${lp.departure})`
            : lp.etcEta
              ? ` (${lp.etcEta})`
              : '';
          textContent += `  - ${idx + 1}. ${lp.loadPort || '—'}${dt}\n`;
          htmlContent += `<li>${idx + 1}. ${lp.loadPort || '—'}${dt}</li>`;
        });
        htmlContent += `</ul>`;
      } else {
        textContent += `• Load Ports: None\n`;
        htmlContent += `<div>Load Ports: None</div>`;
      }

      if (dischPorts.length) {
        textContent += `• Discharge Ports (${dischPorts.length}):\n`;
        htmlContent += `<strong>Discharge Ports (${dischPorts.length}):</strong><ul>`;
        dischPorts.forEach((dp, idx) => {
          const dt = dp.arrival
            ? ` (Arrival: ${dp.arrival})`
            : dp.etcEta
              ? ` (${dp.etcEta})`
              : '';
          textContent += `  - ${idx + 1}. ${dp.dischargePort || '—'}${dt}\n`;
          htmlContent += `<li>${idx + 1}. ${dp.dischargePort || '—'}${dt}</li>`;
        });
        htmlContent += `</ul>`;
      } else {
        textContent += `• Discharge Ports: None\n`;
        htmlContent += `<div>Discharge Ports: None</div>`;
      }
      htmlContent += `</div>`;
    } else if (tileType === 'heatplans') {
      title = 'Heat Plans';
      const timeline = this.modalHeatplans;
      const rows = timeline?.rows || [];
      const stateLabel: Record<HeatplanState, string> = { done: 'Completed', active: 'Active', upcoming: 'Upcoming' };

      textContent = `🚢 ${vesselHeader}
🔥 Heat Plans (${timeline?.count ?? 0}):
`;
      htmlContent = `<div><strong>🚢 ${vesselHeader}</strong><br/><strong>🔥 Heat Plans (${timeline?.count ?? 0}):</strong>`;
      if (rows.length) {
        htmlContent += `<ul>`;
        rows.forEach((r) => {
          const port = this.portOnLatestPlan(r);
          const line = `Plan ${r.plan_number}: ${this.formatHeatplanDate(r.start_date)} → ${this.formatHeatplanDate(r.end_date)} (${r.days} ${r.days === 1 ? 'day' : 'days'}) · ${stateLabel[r.state]}${port ? ` · ${port.arriving ? 'vessel arriving' : 'vessel at'} ${port.name}` : ''}`;
          textContent += `• ${line}
`;
          htmlContent += `<li>${line}</li>`;
        });
        htmlContent += `</ul>`;
      } else {
        textContent += `• None created yet
`;
        htmlContent += `<div>None created yet</div>`;
      }
      htmlContent += `</div>`;
    }

    try {
      if (navigator.clipboard && window.ClipboardItem) {
        const textBlob = new Blob([textContent], { type: 'text/plain' });
        const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/plain': textBlob,
            'text/html': htmlBlob,
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(textContent);
      }
      this.copiedTile = title;
      this.toastr.success(`Copied ${title} data to clipboard!`);
    } catch (err) {
      console.error('Clipboard copy error:', err);
      const textarea = document.createElement('textarea');
      textarea.value = textContent;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.copiedTile = title;
      this.toastr.success(`Copied ${title} data to clipboard!`);
    }

    setTimeout(() => {
      if (this.copiedTile === title) {
        this.copiedTile = null;
      }
    }, 2500);
  }

  selectCategoryView(
    mode: 'overview' | 'consumption' | 'temp' | 'voyage' | 'ports' | 'all',
  ): void {
    this.activeCategoryView = mode;
    if (mode === 'overview') {
      this.consumptionDetailsExpanded = false;
      this.averageTemperatureExpanded = false;
      this.voyageDetailsExpanded = false;
      this.loadPortsExpanded = false;
      this.dischargePortsExpanded = false;
    } else if (mode === 'consumption') {
      this.consumptionDetailsExpanded = true;
      this.averageTemperatureExpanded = false;
      this.voyageDetailsExpanded = false;
      this.loadPortsExpanded = false;
      this.dischargePortsExpanded = false;
    } else if (mode === 'temp') {
      this.consumptionDetailsExpanded = false;
      this.averageTemperatureExpanded = true;
      this.voyageDetailsExpanded = false;
      this.loadPortsExpanded = false;
      this.dischargePortsExpanded = false;
    } else if (mode === 'voyage') {
      this.consumptionDetailsExpanded = false;
      this.averageTemperatureExpanded = false;
      this.voyageDetailsExpanded = true;
      this.loadPortsExpanded = false;
      this.dischargePortsExpanded = false;
    } else if (mode === 'ports') {
      this.consumptionDetailsExpanded = false;
      this.averageTemperatureExpanded = false;
      this.voyageDetailsExpanded = false;
      this.loadPortsExpanded = true;
      this.dischargePortsExpanded = true;
    } else if (mode === 'all') {
      this.consumptionDetailsExpanded = true;
      this.averageTemperatureExpanded = true;
      this.voyageDetailsExpanded = true;
      this.loadPortsExpanded = true;
      this.dischargePortsExpanded = true;
    }
  }

  getConsumptionRowSummary(element: any): string {
    const total =
      element?.fleet_data?.totalHeatingBunkerConsumptionMT ||
      element?.fleet_data?.aeConsumptionForBoilerOperationMT ||
      element?.fleet_data?.estimatedConsumption;
    return total ? `${total} MT` : '—';
  }

  getConsumptionRowTooltip(element: any): string {
    return [
      `AE Consumption: ${element?.fleet_data?.aeConsumptionForBoilerOperationMT || '—'} MT`,
      `Deck Heat Exch.: ${element?.fleet_data?.deckHeatExchangerBunkerConsumptionMT || '—'} MT`,
      `Total Bunker: ${element?.fleet_data?.totalBunkerConsumptionMT || '—'} MT`,
      `Total Heating: ${element?.fleet_data?.totalHeatingBunkerConsumptionMT || '—'} MT`,
      `Est. Plan: ${element?.fleet_data?.estimatedConsumption || '—'}`,
    ].join('\n');
  }

  getAvgTempRowSummary(element: any): string {
    const loaded = element?.fleet_data?.averageLoadedCargoTempDegC;
    const disch = element?.fleet_data?.averageDischCargoTempDegC;
    if (loaded && disch) return `${loaded}° / ${disch}°`;
    if (loaded) return `${loaded}°C`;
    if (disch) return `${disch}°C`;
    return '—';
  }

  getAvgTempRowTooltip(element: any): string {
    return [
      `Avg Loaded Temp: ${element?.fleet_data?.averageLoadedCargoTempDegC || '—'} °C`,
      `Avg Discharge Temp: ${element?.fleet_data?.averageDischCargoTempDegC || '—'} °C`,
    ].join('\n');
  }

  toggleAllDetailSections(event?: Event): void {
    event?.stopPropagation();
    const expand = !this.allDetailSectionsExpanded;
    this.voyageDetailsExpanded = expand;
    this.loadPortsExpanded = expand;
    this.dischargePortsExpanded = expand;
    this.consumptionDetailsExpanded = expand;
    this.averageTemperatureExpanded = expand;
    this.activeCategoryView = expand ? 'all' : 'overview';
  }

  getVoyageDetailsCollapsedSummary(element: any): string {
    const voyageNo = String(element?.voyage_no ?? '').trim();
    const pic = String(element?.pic ?? '').trim();
    if (voyageNo && pic) {
      return `${voyageNo} · ${pic}`;
    }
    return voyageNo || pic || '—';
  }

  getVoyageDetailsTooltip(element: any): string {
    return [
      `Voyage No.: ${element?.voyage_no || '—'}`,
      `PIC: ${element?.pic || '—'}`,
      `Vessel Received Date: ${element?.form_timing_display || '—'}`,
    ].join('\n');
  }

  getLoadPortsCollapsedSummary(element: any): string {
    const details = (element?.fleet_data?.loadPortDetails || []).filter(
      (entry: unknown) => this.hasPortData(entry),
    );
    if (!details.length) {
      return '—';
    }
    const first = details[0]?.loadPort?.trim() || '—';
    if (details.length === 1) {
      return first;
    }
    return `${first} (+${details.length - 1})`;
  }

  getDischargePortsCollapsedSummary(element: any): string {
    const details = (element?.fleet_data?.dischargePortDetails || []).filter(
      (entry: unknown) => this.hasPortData(entry),
    );
    if (!details.length) {
      return '—';
    }
    const first = details[0]?.dischargePort?.trim() || '—';
    if (details.length === 1) {
      return first;
    }
    return `${first} (+${details.length - 1})`;
  }

  getLoadPortsTooltip(element: any): string {
    const details = element?.fleet_data?.loadPortDetails || [];
    const lines = details
      .filter((entry: unknown) => this.hasPortData(entry))
      .map(
        (entry: { loadPort?: string; departure?: string }, index: number) =>
          `${index + 1}. ${entry.loadPort || '—'} — ${entry.departure || '—'}`,
      );
    return lines.length ? lines.join('\n') : 'No load port data';
  }

  getDischargePortsTooltip(element: any): string {
    const details = element?.fleet_data?.dischargePortDetails || [];
    const lines = details
      .filter((entry: unknown) => this.hasPortData(entry))
      .map(
        (entry: { dischargePort?: string; arrival?: string }, index: number) =>
          `${index + 1}. ${entry.dischargePort || '—'} — ${entry.arrival || '—'}`,
      );
    return lines.length ? lines.join('\n') : 'No discharge port data';
  }

  updatePortColumnIndices(data: any[] = this.filteredData): void {
    let maxLoad = 0;
    let maxDischarge = 0;

    (data || []).forEach((item) => {
      const fleetData = item?.fleet_data;
      maxLoad = Math.max(
        maxLoad,
        this.countActivePorts(fleetData?.loadPortDetails),
      );
      maxDischarge = Math.max(
        maxDischarge,
        this.countActivePorts(fleetData?.dischargePortDetails),
      );
    });

    this.loadPortIndices = Array.from(
      { length: Math.max(maxLoad, 1) },
      (_, i) => i,
    );
    this.dischargePortIndices = Array.from(
      { length: Math.max(maxDischarge, 1) },
      (_, i) => i,
    );
  }

  ngOnInit() {
    this.filterForm = this.fb.group({
      vesselIMO: [''],
      segment: [''],
      from_date: [''],
      to_date: [''],
    });

    this.username = localStorage.getItem('username');
    this.loginPic = localStorage.getItem('loginPic');
    this.isClientLogin = this.commonService.isCargoHeatingClientLogin(
      this.username,
    );
    this.showClientFilter = this.commonService.isMerakiOperations(
      this.username,
    );
    // Restore the Completed Voyages view when returning from a completed voyage's detail page
    if (this.route.snapshot.queryParamMap.get('view') === 'completed') {
      this.isSpecialVoyage = true;
      this.callSpecialVoyageApi(true);
      return;
    }

    this.loadNormalRequests(true);
  }

  updateDropdownOptions(data: any[]) {
    this.clientOptions = Array.from(
      new Set(data.map((item: any) => item.client).filter(Boolean)),
    );

    this.cargoOptions = Array.from(
      new Set(data.map((item: any) => item.cargo_type).filter(Boolean)),
    );

    // Segment values come from the single source of truth (New Voyage form),
    // so every Segment filter shows exactly the same values consistently.
    this.segmentOptions = [...CARGO_HEATING_SEGMENTS];

    this.vesselOptions = Array.from(
      new Set(data.map((item: any) => item.vessel_name).filter(Boolean)),
    ).sort();

    this.heatingTypeOptions = Array.from(
      new Set(data.map((item: any) => item.heating_type).filter(Boolean)),
    ).sort();
  }
  // CLIENT

  // CARGO GRADE
  toggleCargoDropdown(event?: Event) {
    if (event) event.stopPropagation();
    this.cargoDropdownOpen = !this.cargoDropdownOpen;
    this.clientDropdownOpen = false;
    this.segmentDropdownOpen = false;
    this.heatingTypeDropdownOpen = false;
    this.vesselDropdownOpen = false;
  }

  onCargoChange(event: any, cargo: string) {
    if (event.target.checked) {
      this.selectedCargos.push(cargo);
    } else {
      this.selectedCargos = this.selectedCargos.filter((c) => c !== cargo);
    }
  }

  toggleAllCargos(event: any) {
    const filtered = this.getFilteredCargos();
    if (event.target.checked) {
      const current = new Set(this.selectedCargos);
      filtered.forEach((c) => current.add(c));
      this.selectedCargos = Array.from(current);
    } else {
      const filteredSet = new Set(filtered);
      this.selectedCargos = this.selectedCargos.filter((c) => !filteredSet.has(c));
    }
  }

  isAllCargosSelected() {
    const filtered = this.getFilteredCargos();
    return (
      filtered.length > 0 &&
      filtered.every((c) => this.selectedCargos.includes(c))
    );
  }

  clearCargos() {
    this.selectedCargos = [];
  }

  toggleHeatingTypeDropdown(event?: Event) {
    if (event) event.stopPropagation();
    this.heatingTypeDropdownOpen = !this.heatingTypeDropdownOpen;
    this.clientDropdownOpen = false;
    this.segmentDropdownOpen = false;
    this.cargoDropdownOpen = false;
    this.vesselDropdownOpen = false;
  }

  onHeatingTypeChange(event: any, heatingType: string) {
    if (event.target.checked) {
      this.selectedHeatingTypes.push(heatingType);
    } else {
      this.selectedHeatingTypes = this.selectedHeatingTypes.filter(
        (type) => type !== heatingType,
      );
    }
  }

  toggleAllHeatingTypes(event: any) {
    const filtered = this.getFilteredHeatingTypes();
    if (event.target.checked) {
      const current = new Set(this.selectedHeatingTypes);
      filtered.forEach((h) => current.add(h));
      this.selectedHeatingTypes = Array.from(current);
    } else {
      const filteredSet = new Set(filtered);
      this.selectedHeatingTypes = this.selectedHeatingTypes.filter(
        (h) => !filteredSet.has(h),
      );
    }
  }

  isAllHeatingTypesSelected() {
    const filtered = this.getFilteredHeatingTypes();
    return (
      filtered.length > 0 &&
      filtered.every((h) => this.selectedHeatingTypes.includes(h))
    );
  }

  clearHeatingTypes() {
    this.selectedHeatingTypes = [];
  }

  getFilteredHeatingTypes() {
    const available = this.getFilteredOptionsForField('heating_type');
    return available.filter((opt: string) =>
      opt.toLowerCase().includes(this.heatingTypeSearchQuery.toLowerCase()),
    );
  }

  // OVERRIDE TOGGLES TO CLOSE OTHERS
  toggleSegmentDropdown(event?: Event) {
    if (event) event.stopPropagation();
    this.segmentDropdownOpen = !this.segmentDropdownOpen;
    this.clientDropdownOpen = false;
    this.cargoDropdownOpen = false;
    this.heatingTypeDropdownOpen = false;
    this.vesselDropdownOpen = false;
  }

  // VESSEL NAME
  toggleVesselDropdown(event?: Event) {
    if (event) event.stopPropagation();
    this.vesselDropdownOpen = !this.vesselDropdownOpen;
    this.clientDropdownOpen = false;
    this.segmentDropdownOpen = false;
    this.cargoDropdownOpen = false;
    this.heatingTypeDropdownOpen = false;
  }

  onVesselChange(event: any, vessel: string) {
    if (event.target.checked) {
      this.selectedVessels.push(vessel);
    } else {
      this.selectedVessels = this.selectedVessels.filter((v) => v !== vessel);
    }
  }

  toggleAllVessels(event: any) {
    const filtered = this.getFilteredVessels();
    if (event.target.checked) {
      const current = new Set(this.selectedVessels);
      filtered.forEach((v) => current.add(v));
      this.selectedVessels = Array.from(current);
    } else {
      const filteredSet = new Set(filtered);
      this.selectedVessels = this.selectedVessels.filter((v) => !filteredSet.has(v));
    }
  }

  isAllVesselsSelected() {
    const filtered = this.getFilteredVessels();
    return (
      filtered.length > 0 &&
      filtered.every((v) => this.selectedVessels.includes(v))
    );
  }

  clearVessels() {
    this.selectedVessels = [];
  }

  // OVERRIDE TOGGLES TO CLOSE OTHERS
  toggleClientDropdown(event?: Event) {
    if (event) event.stopPropagation();
    this.clientDropdownOpen = !this.clientDropdownOpen;
    this.segmentDropdownOpen = false;
    this.cargoDropdownOpen = false;
    this.heatingTypeDropdownOpen = false;
    this.vesselDropdownOpen = false;
  }

  getFilteredOptionsForField(field: string): string[] {
    let data = this.originalData;

    // Filter by Client (excluding itself)
    if (field !== 'client' && this.selectedClients.length > 0) {
      data = data.filter((item: any) =>
        this.selectedClients.includes(item.client),
      );
    }

    // Filter by Vessel (excluding itself)
    if (field !== 'vessel_name' && this.selectedVessels.length > 0) {
      data = data.filter((item: any) =>
        this.selectedVessels.includes(item.vessel_name),
      );
    }

    // Filter by Segment (excluding itself)
    if (field !== 'segment' && this.selectedSegments.length > 0) {
      data = data.filter((item: any) =>
        matchesCargoHeatingSegment(item.segment, this.selectedSegments),
      );
    }

    // Filter by Cargo (excluding itself)
    if (field !== 'cargo_type' && this.selectedCargos.length > 0) {
      data = data.filter((item: any) =>
        this.selectedCargos.includes(item.cargo_type),
      );
    }

    // Filter by Heating Type (excluding itself)
    if (field !== 'heating_type' && this.selectedHeatingTypes.length > 0) {
      data = data.filter((item: any) =>
        this.selectedHeatingTypes.includes(item.heating_type),
      );
    }

    // Get unique values for the target field
    const uniqueValues = Array.from(
      new Set(data.map((item: any) => item[field]).filter(Boolean)),
    ) as string[];
    return uniqueValues.sort();
  }

  getFilteredClients() {
    const available = this.getFilteredOptionsForField('client');
    return available.filter((opt: string) =>
      opt.toLowerCase().includes(this.clientSearchQuery.toLowerCase()),
    );
  }

  getFilteredVessels() {
    const available = this.getFilteredOptionsForField('vessel_name');
    return available.filter((opt: string) =>
      opt.toLowerCase().includes(this.vesselSearchQuery.toLowerCase()),
    );
  }

  getFilteredSegments() {
    const available = this.getFilteredOptionsForField('segment');
    return available.filter((opt: string) =>
      opt.toLowerCase().includes(this.segmentSearchQuery.toLowerCase()),
    );
  }

  getFilteredCargos() {
    const available = this.getFilteredOptionsForField('cargo_type');
    return available.filter((opt: string) =>
      opt.toLowerCase().includes(this.cargoSearchQuery.toLowerCase()),
    );
  }

  onClientChange(event: any, client: string) {
    if (event.target.checked) {
      this.selectedClients.push(client);
    } else {
      this.selectedClients = this.selectedClients.filter((c) => c !== client);
    }
  }

  get hasActiveFilters(): boolean {
    return (
      (this.selectedClients?.length || 0) > 0 ||
      (this.selectedVessels?.length || 0) > 0 ||
      (this.selectedSegments?.length || 0) > 0 ||
      (this.selectedCargos?.length || 0) > 0 ||
      (this.selectedHeatingTypes?.length || 0) > 0 ||
      !!(this.searchQuery || '').trim() ||
      this.filterNotStartedOnly ||
      !!(this.filterForm?.controls['from_date']?.value || this.filterForm?.controls['to_date']?.value)
    );
  }

  /** Quick search across the columns shown in the table. */
  private matchesSearch(item: any, query: string): boolean {
    return [
      item.vessel_name,
      item.vessel_imo,
      item.segment,
      item.client,
      item.pic,
      item.voyage_no,
      item.cargo_type,
      item.heating_type,
      item.voyage_status,
    ].some((value) => String(value ?? '').toLowerCase().includes(query));
  }

  onSearchChange(value: string): void {
    this.searchQuery = value || '';
    this.applyFilters();
  }

  clearSearch(): void {
    this.onSearchChange('');
  }

  /**
   * Summary card shortcuts: Running shows the active voyages, Yet to Start narrows them
   * to voyages that have not started, Completed switches to the completed view.
   */
  showVoyageStatus(status: 'active' | 'not-started' | 'completed'): void {
    if (this.isTableLoading) {
      return;
    }
    if (status === 'completed') {
      this.setVoyageView('completed');
      return;
    }

    const notStartedOnly = status === 'not-started';
    if (this.isSpecialVoyage) {
      // setVoyageView() clears the flag and reloads; the reload re-applies it via applyFilters().
      this.setVoyageView('running');
      this.filterNotStartedOnly = notStartedOnly;
      return;
    }
    this.filterNotStartedOnly = notStartedOnly;
    this.applyFilters();
  }

  get activeVoyageCount(): number {
    return (this.runningCount || 0) + (this.notStartedCount || 0);
  }

  percentOfTotal(count: number): number {
    return this.totalVessels > 0 ? Math.round((count / this.totalVessels) * 100) : 0;
  }

  clearSingleFilter(type: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (type === 'client') {
      this.clearClients();
      this.clientSearchQuery = '';
    } else if (type === 'vessel') {
      this.clearVessels();
      this.vesselSearchQuery = '';
    } else if (type === 'segment') {
      this.clearSegments();
      this.segmentSearchQuery = '';
    } else if (type === 'cargo') {
      this.clearCargos();
      this.cargoSearchQuery = '';
    } else if (type === 'heatingType') {
      this.clearHeatingTypes();
      this.heatingTypeSearchQuery = '';
    }
    this.applyFilters();
  }

  toggleAllClients(event: any) {
    const filtered = this.getFilteredClients();
    if (event.target.checked) {
      const current = new Set(this.selectedClients);
      filtered.forEach((c) => current.add(c));
      this.selectedClients = Array.from(current);
    } else {
      const filteredSet = new Set(filtered);
      this.selectedClients = this.selectedClients.filter((c) => !filteredSet.has(c));
    }
  }

  isAllClientsSelected() {
    const filtered = this.getFilteredClients();
    return (
      filtered.length > 0 &&
      filtered.every((c) => this.selectedClients.includes(c))
    );
  }

  clearClients() {
    this.selectedClients = [];
  }

  onSegmentChange(event: any, seg: string) {
    if (event.target.checked) {
      this.selectedSegments.push(seg);
    } else {
      this.selectedSegments = this.selectedSegments.filter((s) => s !== seg);
    }
  }

  toggleAllSegments(event: any) {
    const filtered = this.getFilteredSegments();
    if (event.target.checked) {
      const current = new Set(this.selectedSegments);
      filtered.forEach((s) => current.add(s));
      this.selectedSegments = Array.from(current);
    } else {
      const filteredSet = new Set(filtered);
      this.selectedSegments = this.selectedSegments.filter((s) => !filteredSet.has(s));
    }
  }

  isAllSegmentsSelected() {
    const filtered = this.getFilteredSegments();
    return (
      filtered.length > 0 &&
      filtered.every((s) => this.selectedSegments.includes(s))
    );
  }

  clearSegments() {
    this.selectedSegments = [];
  }

  /**
   * Parse a date to local midnight. fleet_data dates are DD/MM/YYYY (DD-MM-YYYY also accepted);
   * YYYY-MM-DD and Date objects (date picker) are handled too. A trailing time is ignored.
   */
  private parseFlexibleDate(value: any): number | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      const time = new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
      return isNaN(time) ? null : time;
    }
    const str = String(value).trim();
    if (!str) {
      return null;
    }

    // DD/MM/YYYY or DD-MM-YYYY — never fall through to Date(), which reads it as MM/DD
    const dmy = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:\D|$)/);
    if (dmy) {
      return this.toLocalDate(+dmy[3], +dmy[2], +dmy[1]);
    }

    // YYYY-MM-DD as local midnight, so it lines up with the DD/MM/YYYY dates
    const ymd = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:\D|$)/);
    if (ymd) {
      return this.toLocalDate(+ymd[1], +ymd[2], +ymd[3]);
    }

    const time = new Date(str).getTime();
    return isNaN(time) ? null : time;
  }

  /** Local-midnight timestamp, or null for impossible dates such as 31/02. */
  private toLocalDate(year: number, month: number, day: number): number | null {
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return null;
    }
    return date.getTime();
  }

  /** Latest arrival date across the voyage's discharge ports (as shown in Port schedule); drives sort and from/to filter. */
  private getLatestDischargeDate(item: any): number | null {
    const times = (item?.fleet_data?.dischargePortDetails || [])
      .map((entry: any) => this.parseFlexibleDate(entry?.arrival))
      .filter((time: number | null): time is number => time !== null);
    return times.length ? Math.max(...times) : null;
  }

  /** Default table order: latest discharge date first, voyages without one last, then newest voyage_id. */
  private compareByLatestDischarge(a: any, b: any): number {
    const timeA = this.getLatestDischargeDate(a);
    const timeB = this.getLatestDischargeDate(b);
    if (timeA !== timeB) {
      if (timeA === null) return 1;
      if (timeB === null) return -1;
      return timeB - timeA;
    }
    return Number(b.voyage_id || 0) - Number(a.voyage_id || 0);
  }

  /** Default table order: next plan due for active voyages, latest discharge date for completed ones. */
  private compareDefault(a: any, b: any, completed: boolean = this.isSpecialVoyage): number {
    return completed ? this.compareByLatestDischarge(a, b) : this.compareByNextPlanDue(a, b);
  }

  applyFilters(): void {
    const vesselIMO =
      this.filterForm?.get('vesselIMO')?.value?.toString().trim() || '';

    const fromDateValue = this.filterForm?.get('from_date')?.value || '';
    const toDateValue = this.filterForm?.get('to_date')?.value || '';
    const fromTime = fromDateValue
      ? this.parseFlexibleDate(fromDateValue)
      : null;
    // Include the whole "to" day by pushing to end-of-day
    let toTime = toDateValue ? this.parseFlexibleDate(toDateValue) : null;
    if (toTime !== null) {
      toTime += 24 * 60 * 60 * 1000 - 1;
    }

    const q = (this.searchQuery || '').trim().toLowerCase();

    this.filteredData = (this.originalData || []).filter((item: any) => {
      if (this.isSpecialVoyage && !this.isCompletedVessel(item)) {
        return false;
      }
      if (!this.isSpecialVoyage && this.isCompletedVessel(item)) {
        return false;
      }

      if (fromTime !== null || toTime !== null) {
        const itemTime = this.getLatestDischargeDate(item);
        if (itemTime === null) {
          return false;
        }
        if (fromTime !== null && itemTime < fromTime) {
          return false;
        }
        if (toTime !== null && itemTime > toTime) {
          return false;
        }
      }

      if (this.filterNotStartedOnly && !this.isNotStartedVessel(item)) {
        return false;
      }

      if (vesselIMO && !String(item.vessel_imo).includes(vesselIMO)) {
        return false;
      }

      if (q && !this.matchesSearch(item, q)) {
        return false;
      }

      return (
        (this.selectedClients.length === 0 ||
          this.selectedClients.includes(item.client)) &&
        (this.selectedVessels.length === 0 ||
          this.selectedVessels.includes(item.vessel_name)) &&
        (this.selectedSegments.length === 0 ||
          matchesCargoHeatingSegment(item.segment, this.selectedSegments)) &&
        (this.selectedCargos.length === 0 ||
          this.selectedCargos.includes(item.cargo_type)) &&
        (this.selectedHeatingTypes.length === 0 ||
          this.selectedHeatingTypes.includes(item.heating_type))
      );
    });

    this.resetToFirstPage();
    this.updatePortColumnIndices(this.filteredData);
    this.getDataCounts(this.filteredData);
    this.recomputeCollapsedRailOverallSummaries();
  }

  clearDateFilter(event?: Event): void {
    if (event) event.stopPropagation();
    this.filterForm?.get('from_date')?.setValue(null);
    this.filterForm?.get('to_date')?.setValue(null);
    this.applyFilters();
  }

  filterClick(): void {
    this.applyFilters();
    // Keep the default order on Apply Filter click
    this.filteredData = (this.filteredData || []).sort((a: any, b: any) =>
      this.compareDefault(a, b),
    );
    this.resetToFirstPage();
  }

  toggleNotStartedFilter(): void {
    if (this.isSpecialVoyage) {
      return;
    }

    this.filterNotStartedOnly = !this.filterNotStartedOnly;
    this.applyFilters();
  }

  loadVoyageSummary(callback?: () => void) {
    this.isCardsLoading = true;
    this.commonService.getVoyageBreakup().subscribe({
      next: (res: any) => {
        console.log('Voyage breakup response:', res);

        this.totalVessels = res?.Total || 0;
        this.completedCount = res?.Completed || 0;
        this.runningCount = res?.Running || 0;
        this.notStartedCount = res?.['Not Started'] || 0;
        this.logsInsertedCount = res?.['Logs Inserted'] || 0;
        this.isCardsLoading = false;
        if (callback) {
          callback();
        }
      },

      error: (err) => {
        console.error('Voyage breakup API error:', err);
        this.isCardsLoading = false;
        if (callback) {
          callback();
        }
      },
    });
  }

  convertToDate(str: string): Date {
    const parts = str.split('-'); // DD-MM-YYYY
    return new Date(+parts[2], +parts[1] - 1, +parts[0]);
  }

  openVoyageMonitoring(row: any, event?: Event): void {
    event?.stopPropagation();
    this.editRow(row, true);
  }

  editRow(row: any, openMonitoring = false) {
    this.isNavigatingRow = true;
    this.navigatingVesselName = row.vessel_name || '';

    const payload = {
      vessel_name: row.vessel_name?.trim(),
      vessel_imo: Number(row.vessel_imo),
      voyage_no: String(row.voyage_no),
      cargo_type: row.cargo_type,
      segment: row.segment,
      client: row.client,
      voyage_id: row.voyage_id,
    };

    this.commonService.requestFormFilter(payload).subscribe({
      next: (response) => {
        if (response && response.length > 0) {
          let data = response[0];

          // parse "others"
          if (data.others) {
            try {
              data.others = JSON.parse(data.others);
            } catch (e) {
              console.warn('Error parsing others JSON', e);
            }
          }

          this.commonService.clearHeatPlanFormContext();
          this.commonService.setFleetViewRowDat(data);
          this.commonService.setVoyageListSource(
            this.isSpecialVoyage ? 'completed' : 'ongoing',
          );

          if (this.isClientLogin || openMonitoring) {
            this.router
              .navigate(['/ch-requestForm/voyage-monitoring'], {
                queryParams: {
                  voyage_id: data.voyage_id || row.voyage_id,
                  vessel_name: data.vessel_name || row.vessel_name,
                  vessel_imo: data.vessel_imo || row.vessel_imo,
                  voyage_no: data.voyage_no || row.voyage_no,
                  voyage_status: this.getVoyageStatus(data || row),
                  cargo_type: data.cargo_type || row.cargo_type || '',
                },
              })
              .then(() => {
                this.isNavigatingRow = false;
              });
          } else {
            this.router.navigate(['/ch-requestForm/heat-plan']).then(() => {
              this.isNavigatingRow = false;
            });
          }
        } else {
          this.isNavigatingRow = false;
        }
      },
      error: (err) => {
        console.error('Error in editRow:', err);
        this.isNavigatingRow = false;
      },
    });
  }
  clearClick(): void {
    if (this.filterForm) {
      this.filterForm.reset();
    }
    this.clearClients();
    this.clearSegments();
    this.clearCargos();
    this.clearHeatingTypes();
    this.clearVessels();

    // Clear search queries
    this.searchQuery = '';
    this.clientSearchQuery = '';
    this.vesselSearchQuery = '';
    this.segmentSearchQuery = '';
    this.cargoSearchQuery = '';
    this.heatingTypeSearchQuery = '';
    this.filterNotStartedOnly = false;

    this.filteredData = [...(this.originalData || [])];
    this.resetToFirstPage();
    this.updatePortColumnIndices(this.filteredData);
    this.getDataCounts(this.filteredData);
    this.recomputeCollapsedRailOverallSummaries();
  }

  // filterClick(): void {
  //   const filterValues = this.filterForm.value;

  //   this.filteredData = this.originalData.filter((item: any) =>
  //     (!filterValues.pic || item.pic?.toLowerCase().includes(filterValues.pic.toLowerCase())) &&
  //     (!filterValues.picAnalyst || item.analystPic?.toLowerCase().includes(filterValues.picAnalyst.toLowerCase())) &&
  //     (!filterValues.clientsName || item.clientsName === filterValues.clientsName) &&
  //     (!filterValues.voyageRunningOrCompleted || item.voyageRunningOrCompleted?.toLowerCase().includes(filterValues.voyageRunningOrCompleted.toLowerCase())) &&
  //     (!filterValues.vesselName || item.vessel?.toLowerCase().includes(filterValues.vesselName.toLowerCase()))
  //   );

  //   this.currentPage = 0;
  //   this.paginatedData = this.filteredData.slice(0, this.itemsPerPage);
  //   console.log('Filtered data count:', this.filteredData.length);
  //   this.getDataCounts(this.filteredData);
  // }

  paginateData(): void {
    const data = this.filteredData || [];
    const startIndex = this.currentPage * this.itemsPerPage;
    this.paginatedData = data.slice(startIndex, startIndex + this.itemsPerPage);
  }

  resetToFirstPage(): void {
    this.currentPage = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.paginateData();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.itemsPerPage = event.pageSize;
    this.paginateData();
  }

  // clearClick(): void {
  //   this.filterForm.reset();
  //   this.filteredData = [...this.originalData];
  //   this.paginatedData = this.filteredData.slice(0, this.itemsPerPage);
  //   this.currentPage = 0;
  //   console.log('Clear clicked - refreshing vessel counts');
  //   this.getDataCounts(this.filteredData);

  // }

  loadTableData(): void {
    this.commonService.getDatabaseData().subscribe((data: any[]) => {
      this.paginatedData = data;
    });
  }
  /** Table-only stats — voyage summary cards use loadVoyageSummary() API counts only. */
  getDataCounts(data: any) {
    this.activeVessels = data.filter(
      (vessel: any) =>
        vessel.voyageRunningOrCompleted?.toLowerCase() === 'completed',
    ).length;

    this.inactiveVessels = data.filter(
      (vessel: any) =>
        vessel.voyageRunningOrCompleted?.toLowerCase() === 'running',
    ).length;
  }
  /** Safely stringify + escape a single CSV cell (handles null/undefined). */
  private csvCell(value: any): string {
    if (value === null || value === undefined) {
      return '""';
    }
    const text = String(value).replace(/"/g, '""');
    return `"${text}"`;
  }

  downloadCSV() {
    // Export the full currently-filtered dataset (not just the visible page)
    const data: any[] = this.filteredData || this.originalData || [];

    if (!data.length) {
      return;
    }

    // Determine max port counts across the dataset so every row lines up
    let maxLoadPorts = 1;
    let maxDischargePorts = 1;
    data.forEach((row: any) => {
      const loadPorts = row?.fleet_data?.loadPortDetails?.length || 0;
      const dischargePorts = row?.fleet_data?.dischargePortDetails?.length || 0;
      if (loadPorts > maxLoadPorts) maxLoadPorts = loadPorts;
      if (dischargePorts > maxDischargePorts)
        maxDischargePorts = dischargePorts;
    });

    // Build headers to mirror the on-screen table columns
    const headers: string[] = ['S.No', 'Vessel Name', 'Vessel IMO', 'Segment'];
    if (this.showClientFilter) {
      headers.push('Client');
    }
    if (!this.isClientLogin) {
      headers.push('PIC');
    }
    headers.push('Status', 'Cargo Grade', 'Heating Type');
    if (this.isSpecialVoyage) {
      headers.push('Report Sent Date');
    }
    headers.push(
      'Heating Instructions',
      'AE Consumption',
      'Deck Heat Exchanger Consumption',
      'Total Bunker Consumption',
      'Total Heating Consumption',
      'Estimated Consumption',
      'Estimated Consumption - HeatPlan',
      'Avg Loaded Temp (°C)',
      'Avg Discharge Temp (°C)',
      'Voyage No.',
      'Vessel Received Date',
    );
    for (let i = 0; i < maxLoadPorts; i++) {
      headers.push(`Load Port ${i + 1}`, `Load Port ${i + 1} Departure`);
    }
    for (let i = 0; i < maxDischargePorts; i++) {
      headers.push(
        `Discharge Port ${i + 1}`,
        `Discharge Port ${i + 1} Arrival`,
      );
    }

    const rows: string[] = data.map((row: any, index: number) => {
      const fd = row?.fleet_data || {};
      const values: any[] = [
        index + 1,
        row.vessel_name,
        row.vessel_imo,
        row.segment,
      ];
      if (this.showClientFilter) {
        values.push(row.client);
      }
      if (!this.isClientLogin) {
        values.push(row.pic);
      }
      values.push(
        this.getVoyageStatus(row),
        row.cargo_type,
        row.heating_type || fd.heatingCoilsOrDeckHeatExchanger,
      );
      if (this.isSpecialVoyage) {
        values.push(fd.reportSentDate);
      }
      values.push(
        fd.heatingInstructions,
        fd.aeConsumptionForBoilerOperationMT,
        fd.deckHeatExchangerBunkerConsumptionMT,
        fd.totalBunkerConsumptionMT,
        fd.totalHeatingBunkerConsumptionMT,
        fd.merakiTotalEstimatedHeatingConsumptionsMT,
        fd.estimatedConsumption,
        fd.averageLoadedCargoTempDegC,
        fd.averageDischCargoTempDegC,
        row.voyage_no,
        row.form_timing_display,
      );
      for (let i = 0; i < maxLoadPorts; i++) {
        values.push(
          fd.loadPortDetails?.[i]?.loadPort,
          fd.loadPortDetails?.[i]?.departure,
        );
      }
      for (let i = 0; i < maxDischargePorts; i++) {
        values.push(
          fd.dischargePortDetails?.[i]?.dischargePort,
          fd.dischargePortDetails?.[i]?.arrival,
        );
      }
      return values.map((v) => this.csvCell(v)).join(',');
    });

    const csvContent =
      headers.map((h) => this.csvCell(h)).join(',') + '\n' + rows.join('\n');

    const fileName = this.isSpecialVoyage
      ? 'Completed_Voyages.csv'
      : 'Ongoing_Voyages.csv';

    const blob = new Blob(['\uFEFF' + csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
  ngAfterViewInit() {
    if (this.sort) {
      this.sort.sortChange.subscribe((sort: Sort) => {
        this.sortData(sort);
      });
    }
  }

  sortData(sort: Sort) {
    if (!this.filteredData || !Array.isArray(this.filteredData)) {
      return;
    }
    const data = this.filteredData.slice();
    if (sort.active === 'hp_due' && sort.direction !== '') {
      // Ascending = nearest "send by" date first. Voyages without a due date stay at the bottom either way.
      const dir = sort.direction === 'desc' ? -1 : 1;
      this.filteredData = data.sort((a: any, b: any) => {
        const ua = this.heatplanDueRank(a);
        const ub = this.heatplanDueRank(b);
        if (ua === null || ub === null) {
          return ua === ub ? 0 : ua === null ? 1 : -1;
        }
        return (ua - ub) * dir;
      });
    } else if (!sort.active || sort.direction === '') {
      this.filteredData = data.sort((a: any, b: any) =>
        this.compareDefault(a, b),
      );
    } else {
      const isAsc = sort.direction === 'asc';
      this.filteredData = data.sort((a: any, b: any) => {
        return this.compare(a, b, sort.active, isAsc);
      });
    }

    this.paginateData();
  }

  getSortProperty(obj: any, sortActive: string): any {
    switch (sortActive) {
      case 'sno':
        return Number(obj.voyage_id || 0); // Use voyage_id as proxy for insertion order
      case 'vessel_name':
        return obj.vessel_name || obj.vesselName || '';
      case 'vessel_imo':
        return obj.vessel_imo || obj.vesselImo || '';
      case 'segment':
        return obj.segment || '';
      case 'client':
        return obj.client || obj.client_name || obj.clientsName || '';
      case 'pic':
        return obj.pic || '';
      case 'voyage_status':
        return this.getVoyageStatus(obj) || obj.voyage_status || '';
      case 'cargo_type':
        return obj.cargo_type || obj.grade || obj.cargoType || '';
      case 'heating_type':
        return (
          obj.heating_type || obj.fleet_data?.heatingCoilsOrDeckHeatExchanger || ''
        );
      case 'hp_due':
        return this.heatplanDueRank(obj);
      case 'heating_instructions':
        return obj.fleet_data?.heatingInstructions;
      case 'report_sent_date':
        return obj.fleet_data?.reportSentDate;
      case 'aeConsumption':
        return obj.fleet_data?.aeConsumptionForBoilerOperationMT;
      case 'deckHeatConsumption':
        return obj.fleet_data?.deckHeatExchangerBunkerConsumptionMT;
      case 'totalBunker':
        return obj.fleet_data?.totalBunkerConsumptionMT;
      case 'totalHeating':
        return obj.fleet_data?.totalHeatingBunkerConsumptionMT;
      case 'estimatedHeating':
        return obj.fleet_data?.merakiTotalEstimatedHeatingConsumptionsMT;
      case 'estimatedPlan':
        return obj.fleet_data?.estimatedConsumption;
      case 'avgLoadedTemp':
        return obj.fleet_data?.averageLoadedCargoTempDegC;
      case 'avgDischargeTemp':
        return obj.fleet_data?.averageDischCargoTempDegC;
      default: {
        if (sortActive.startsWith('loadPort')) {
          const index = parseInt(sortActive.replace('loadPort', ''), 10) - 1;
          return obj.fleet_data?.loadPortDetails?.[index]?.loadPort;
        } else if (sortActive.startsWith('loadDeparture')) {
          const index =
            parseInt(sortActive.replace('loadDeparture', ''), 10) - 1;
          return obj.fleet_data?.loadPortDetails?.[index]?.departure;
        } else if (sortActive.startsWith('dischargePort')) {
          const index =
            parseInt(sortActive.replace('dischargePort', ''), 10) - 1;
          return obj.fleet_data?.dischargePortDetails?.[index]?.dischargePort;
        } else if (sortActive.startsWith('dischargeArrival')) {
          const index =
            parseInt(sortActive.replace('dischargeArrival', ''), 10) - 1;
          return obj.fleet_data?.dischargePortDetails?.[index]?.arrival;
        }
        return obj[sortActive];
      }
    }
  }

  compare(aObj: any, bObj: any, sortActive: string, isAsc: boolean) {
    const a = this.getSortProperty(aObj, sortActive);
    const b = this.getSortProperty(bObj, sortActive);

    if (a === null || a === undefined || a === '') {
      return isAsc ? 1 : -1;
    }
    if (b === null || b === undefined || b === '') {
      return isAsc ? -1 : 1;
    }

    // Handle date strings in the format "YYYY/MM/DD-HH:MM:SS-UTC"
    const dateRegex = /^\d{4}\/\d{2}\/\d{2}-\d{2}:\d{2}:\d{2}-UTC$/;
    if (
      typeof a === 'string' &&
      typeof b === 'string' &&
      dateRegex.test(a) &&
      dateRegex.test(b)
    ) {
      const aDate = new Date(a.replace('-UTC', 'Z'));
      const bDate = new Date(b.replace('-UTC', 'Z'));
      return (aDate.getTime() - bDate.getTime()) * (isAsc ? 1 : -1);
    }

    if (typeof a === 'string' && typeof b === 'string') {
      const dateA = Date.parse(a);
      const dateB = Date.parse(b);
      if (
        !isNaN(dateA) &&
        !isNaN(dateB) &&
        a.includes('-') &&
        b.includes('-')
      ) {
        return (dateA - dateB) * (isAsc ? 1 : -1);
      }
    }

    // Handle numeric values / numeric strings (e.g. IMO numbers, counts, voyage IDs)
    const aNum =
      typeof a === 'number'
        ? a
        : typeof a === 'string' && /^\d+(\.\d+)?$/.test(a.trim())
          ? parseFloat(a.trim())
          : NaN;
    const bNum =
      typeof b === 'number'
        ? b
        : typeof b === 'string' && /^\d+(\.\d+)?$/.test(b.trim())
          ? parseFloat(b.trim())
          : NaN;
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return (aNum - bNum) * (isAsc ? 1 : -1);
    }

    // Handle string sorting (case insensitive natural sorting)
    if (typeof a === 'string' && typeof b === 'string') {
      return (
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }) *
        (isAsc ? 1 : -1)
      );
    }

    // Fallback for other types
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = date.getUTCDate().toString().padStart(2, '0');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0'); // Month is zero-based
    const year = date.getUTCFullYear().toString().slice(-2); // Get last two digits of the year
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    const seconds = date.getUTCSeconds().toString().padStart(2, '0');

    return `${day} - ${month} - ${year} ${hours}:${minutes}-UTC`;
  }

  filterShipNames(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value
      .trim()
      .toLowerCase();

    const segment = this.filterForm
      .get('segment')
      ?.value?.trim()
      ?.toLowerCase();

    const uniqueNames = new Set<string>();

    this.shipNameSuggestions = this.originalData.filter((item: any) => {
      const vesselName = item.vessel_name?.toLowerCase();
      const itemSegment = item.segment?.toLowerCase();

      const segmentMatches = !segment || itemSegment === segment;
      const nameMatches = vesselName?.includes(inputValue);

      if (segmentMatches && nameMatches && !uniqueNames.has(vesselName)) {
        uniqueNames.add(vesselName);
        return true;
      }

      return false;
    });
  }
  convertDate(inputDate: string): string {
    if (!inputDate?.trim()) return '';
    const date = new Date(inputDate); // Convert string to Date object

    // Format the date as YYYY-MM-DD
    const formattedDate = date.toISOString().split('T')[0];

    return formattedDate;
  }
  formatCustomDate(dateStr: string): string {
    if (!dateStr?.trim()) return '';
    // Remove '-UTC' and trim
    const cleanedStr = dateStr.replace('-UTC', '').trim();

    // Match the date part using regex
    const match = cleanedStr.match(/(\d{2})\s*-\s*(\d{2})\s*-\s*(\d{2})/);

    if (!match) return 'Invalid date';

    const [, day, month, year] = match;

    // Convert 2-digit year to full year
    const fullYear = parseInt(year, 10) < 50 ? '20' + year : '19' + year;

    // Return with day hardcoded to 01 as you requested
    return `${fullYear}-${month.padStart(2, '0')}-01`;
  }
  /** Applies a Light Mode host class so scoped light-theme styles can activate. */
  @HostBinding('class.ch-light') get chLight(): boolean {
    return !this.isDarkMode();
  }

  isDarkMode() {
    return this.requestFormService.isDarkMode();
  }

  getColor(value: number): string {
    if (value > 0) {
      return '#a8d296'; // Positive values in green
    } else if (value < 0) {
      return '#ff9090'; // Negative values in red
    }
    return 'inherit'; // Default color for zero
  }

  downloadClick() {
    const data = this.filteredData || this.elements;

    // Define the columns for your Excel-like format
    const csvHeader = [
      'Ship Name',
      'Year Built',
      'Segment',
      'Start Port',
      'Start Time',
      'End Port',
      'End Time',
      'Condition',
      'CP Speed',
      'Cp Consumption',
      'Days At Sea',
      'Total Fuel L/G',
      'Total Time L/G',
      'Total Dist.',
      'Total Time',
      'Total Bunker Cons',
      'Average Speed',
      'Total Avg Daily Cons',
      'Gw Distance',
      'GW Time',
      'GW Pct.',
      'GW Avg Speed',
      'GW Consumption',
      'GW Avg Daily Cons',
      'Adverse Wx Dist.',
      'Adverse Wx Time',
      'Avg Speed in Adverse Wx Time',
      'Avg Cons in Adverse Wx Time',
    ];

    // Map the data to match the Excel format
    const csvData = data
      .map((item: any, index: any) => [
        item.vessel_name,
        item.year_built,
        item.segment,
        item.start_port,
        item.start_date,
        item.end_port,
        item.end_date,
        item.voyage_type,
        item.cp_speed,
        item.cp_consumption,
        item.daysAtSea,
        item.totalFuelLg,
        item.totalTimeLg,
        item.total_distance,
        item.total_time,
        item.total_bunkers_consumed_at_sea,
        item.avg_speed,
        item.total_avg_daily_cons,
        item.gw_distance,
        item.gw_time,
        item.GW_Pcx,
        item.avg_speed_in_gw,
        item.GW_cons,
        item.gwAvgDailyCons,
        item.adverse_weather_distance,
        item.adverse_weather_time,
        item.avg_speed_in_adverse_wx,
        item.avg_cons_in_adverser_wx,
      ])
      .join('\n');

    // Create CSV content to mimic an Excel sheet structure
    const csvContent = `${csvHeader}\n${csvData}`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Database.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  loadAllRequestsData() {
    this.loadNormalRequests(true);
  }
  newForm() {
    if (this.isNewFormLoading) return;
    console.log('New Form button clicked');
    this.isNewFormLoading = true;

    this.commonService.getAllClients().subscribe({
      next: (res: any) => {
        this.isNewFormLoading = false;
        let clients: string[] = [];
        if (Array.isArray(res)) {
          clients = res.map((item: any) =>
            typeof item === 'string'
              ? item
              : item.client_name || item.name || item.client || String(item),
          );
        } else if (res && typeof res === 'object') {
          const rawList =
            res.clients || res.data || res.client_names || res.result || [];
          if (Array.isArray(rawList)) {
            clients = rawList.map((item: any) =>
              typeof item === 'string'
                ? item
                : item.client_name || item.name || item.client || String(item),
            );
          }
        }
        this.openNewPlanPopup(clients.length > 0 ? clients : null);
      },
      error: (err: any) => {
        this.isNewFormLoading = false;
        console.error(
          'Failed to fetch client names before opening popup:',
          err,
        );
        this.openNewPlanPopup(null);
      },
    });
  }

  private openNewPlanPopup(clientOptions: string[] | null) {
    const dialogRef = this.dialog.open(NewPlanPopupComponent, {
      width: '650px',
      height: 'auto',
      disableClose: true,
      panelClass: 'modern-dialog-panel',
      data: {
        title: 'New Plan',
        message: 'Are you sure you want to create a new Heat Plan?',
        clientOptions: clientOptions,
      },
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.router.navigate(['/ch-allRequests']);
        this.loadAllRequestsData();
      }
    });
  }
  loadNormalRequests(loadCards: boolean = true) {
    const fetchTableData = () => {
      this.isTableLoading = true;
      this.commonService.getallrequestsData().subscribe({
        next: (response: any[]) => {
          const mapped = this.mapRequestsForView(response, 'active');

          this.originalData = this.commonService.filterDataByClient(
            mapped,
            this.username,
          );
          if (this.loginPic === 'demo') {
            this.originalData = this.originalData
              .filter((vessel: any) =>
                DEMO_VESSEL_IMOS.includes(String(vessel.vessel_imo)),
              )
              .map((vessel: any, index: number) => ({
                ...vessel,
                vessel_imo: 'XXXXXXX',
                vessel_name: `Vessel ${index + 1}`,
                client: `Client ${index + 1}`
              }));
          }
          this.updateDropdownOptions(this.originalData);
          // Re-apply the active filters and search so they survive a view switch
          this.applyFilters();
          this.isTableLoading = false;
        },
        error: () => {
          this.isTableLoading = false;
        },
      });
    };

    if (loadCards) {
      this.loadVoyageSummary(() => {
        fetchTableData();
      });
    } else {
      fetchTableData();
    }
  }

  @ViewChild('filterContainer') filterContainer!: ElementRef;

  @HostListener('document:click', ['$event'])
  clickout(event: any) {
    if (
      this.filterContainer &&
      !this.filterContainer.nativeElement.contains(event.target)
    ) {
      this.isFilterOpen = false;
      this.clientDropdownOpen = false;
      this.segmentDropdownOpen = false;
      this.cargoDropdownOpen = false;
      this.heatingTypeDropdownOpen = false;
      this.vesselDropdownOpen = false;
    }
  }

  toggleFilter(event: Event) {
    event.stopPropagation();
    this.isFilterOpen = !this.isFilterOpen;
  }

  setVoyageView(view: 'running' | 'completed') {
    const isCompleted = view === 'completed';
    if (this.isSpecialVoyage === isCompleted) {
      return;
    }
    this.isSpecialVoyage = isCompleted;
    this.filterNotStartedOnly = false;

    if (this.isSpecialVoyage) {
      // Completed voyages: update table only; keep top summary cards unchanged.
      this.callSpecialVoyageApi(false);
    } else {
      // Running voyages: update table only; keep top summary cards unchanged.
      this.loadNormalRequests(false);
    }
  }

  onSpecialVoyageToggle(event: any) {
    const checked = event.target ? event.target.checked : event.checked;
    this.setVoyageView(checked ? 'completed' : 'running');
  }

  callSpecialVoyageApi(loadCards: boolean = true) {
    const fetchSpecialData = () => {
      this.isTableLoading = true;
      this.commonService.specialVoyageApi().subscribe({
        next: (res: any[]) => {
          const mapped = this.mapRequestsForView(res, 'completed');

          this.originalData = this.commonService.filterDataByClient(
            mapped,
            this.username,
          );
          if (this.loginPic === 'demo') {
            this.originalData = this.originalData
              .filter((vessel: any) =>
                DEMO_VESSEL_IMOS.includes(String(vessel.vessel_imo)),
              )
              .map((vessel: any, index: number) => ({
                ...vessel,
                vessel_imo: 'XXXXXXX',
                vessel_name: `Vessel ${index + 1}`,
                client: `Client ${index + 1}`
              }));
          }
          this.updateDropdownOptions(this.originalData);
          // Re-apply the active filters and search so they survive a view switch
          this.applyFilters();
          this.isTableLoading = false;
        },

        error: (err: any) => {
          console.error(err);
          this.toastr.error('Failed to load special voyage data');
          this.isTableLoading = false;
        },
      });
    };

    if (loadCards) {
      this.loadVoyageSummary(() => {
        fetchSpecialData();
      });
    } else {
      fetchSpecialData();
    }
  }

  formatVesselName(name: string): string {
    if (!name) return '';

    return name
      .toLowerCase()
      .split(' ')
      .filter((word) => word.trim().length > 0)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
interface AverageResult {
  vessel: string;
  segment: string;
  averageSpeed: number;
  averageDailyCons: number;
  segmentAvgSpeed: number;
  consAtAvgSegSpeed: number;
  average: number;
  totalDistance: number;
}
interface DatabaseItem {
  pic: string;
  analystPic: string;
  clientsName: string;
  voyageRunningOrCompleted: string;
  // add other fields if needed
}
